import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DirectionsBusOutlinedIcon from "@mui/icons-material/DirectionsBusOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  assignBillingSubscription,
  listAdminResellers,
  convertPlatformWorkspacePresentation,
  discardPlatformWorkspacePresentation,
  excludePlatformDominio,
  getBillingPlanCatalog,
  getPlatformDominioAdminDetail,
  getPlatformDominioHealthSnapshot,
  getPlatformDominioOfferAnalytics,
  getPlatformDominioSuperlogicaLink,
  getPlatformDominioSuperlogicaSummary,
  getPlatformDominioSuperlogicaCobrancas,
  getPlatformDominioUsageSummary,
  getPlatformDominioWorkspaceSegment,
  getPlatformWorkspacePresentationStatus,
  listPlatformBillingAudit,
  postPlatformDominioSuperlogicaLink,
  deletePlatformDominioSuperlogicaLink,
  listPlatformDominioInvoices,
  postPlatformDominioSyncTenantLogin,
  setPlatformDominioWorkspaceSegment,
  listPlatformWorkspaceSegmentCatalog,
  getPlatformDominioAiWorkspaceContext,
  setPlatformDominioAiWorkspaceContext,
  getPlatformDominioPassengerSales,
  getPlatformDominioGupshupReconciliation,
} from "../../../api/client";
import PresentationWorkspaceBanner from "../../../components/workspaces/PresentationWorkspaceBanner";
import { openWorkspaceInApp } from "../../Partner/PartnerConsole/openWorkspaceInApp";
import { resolveTenantDatabaseName } from "../../../utils/resolveTenantDatabaseName";
import { clearStoredWorkspaceIfExcluded } from "../../../utils/workspaceLocalStorage";
import { ALL_WORKSPACE_SEGMENTS, WorkspaceSegments } from "../../../constants/workspaceSegments";
import { BusyButton } from "./shared";
import {
  PLATFORM_CONSOLE_PATH,
  WORKSPACE_DETAIL_SECTION_SLUGS,
  workspaceDetailPath,
  workspaceDetailSectionToIndex,
} from "./platformConsoleRoutes";
import { usePlatformConsoleAcl } from "./PlatformConsoleAclContext";

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("pt-BR");
}

/** Data curta para períodos de cobrança (menos ruído que data+hora completa). */
function fmtDateShort(v, localeTag = "pt-BR") {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(localeTag, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function billingPeriodWholeDays(startRaw, endRaw) {
  const a = new Date(startRaw).getTime();
  const b = new Date(endRaw).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const diff = b - a;
  if (diff <= 0) return null;
  return Math.max(1, Math.round(diff / 86400000));
}

/** 0–100: posição atual no intervalo de cobrança (para barra de progresso). */
function billingPeriodElapsedPct(startRaw, endRaw) {
  const a = new Date(startRaw).getTime();
  const b = new Date(endRaw).getTime();
  const n = Date.now();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
  if (n <= a) return 0;
  if (n >= b) return 100;
  return Math.min(100, Math.max(0, Math.round(((n - a) / (b - a)) * 1000) / 10));
}

function fmtMinutesToShortLabel(totalMinutesRaw, locale = undefined) {
  const totalMinutes = Number(totalMinutesRaw);
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return "—";
  const mins = Math.round(totalMinutes);
  if (mins < 60) return `${mins.toLocaleString(locale)} min`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return `${hours.toLocaleString(locale)}h ${remMins.toLocaleString(locale)}m`;
  const days = Math.floor(hours / 24);
  const remHrs = hours % 24;
  return `${days.toLocaleString(locale)}d ${remHrs.toLocaleString(locale)}h`;
}

function centsToBrl(c) {
  const n = Number(c || 0) / 100;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Mesma convenção da API: subdomínio único + .xpro.me → nome do schema no tenant. */
function inferSchemaFromHost(dominioRaw) {
  const dom = String(dominioRaw || "").trim().toLowerCase();
  if (!dom) return "";
  const host = dom.replace(/^https?:\/\//i, "").split("/")[0].split(":")[0];
  if (!host || !host.endsWith(".xpro.me")) return "";
  const schema = host.slice(0, -".xpro.me".length);
  if (!schema || schema.includes(".")) return "";
  return schema;
}

/** Total aproximado retornado pela Partner API (/usage), quando existir. */
function gupshupApproxFromUsage(usage) {
  if (!usage || usage.approximate_total == null) return null;
  const n = Number(usage.approximate_total);
  return Number.isFinite(n) ? n : null;
}

/**
 * Heurística para ops: compara contagem na plataforma vs referência Gupshup.
 * @returns {"aligned"|"review"|"no_total"|"api_error"}
 */
function gupshupComparisonKind(platformMsgs, usage) {
  const ok = Boolean(usage?.ok);
  const g = gupshupApproxFromUsage(usage);
  const p = Number(platformMsgs);
  const pSafe = Number.isFinite(p) ? p : 0;
  if (!ok && g == null) return "api_error";
  if (g == null) return "no_total";
  if (g === 0 && pSafe === 0) return "aligned";
  if (g === 0) return "review";
  const ratio = Math.abs(pSafe - g) / Math.max(g, 1);
  if (ratio <= 0.2) return "aligned";
  if (ratio <= 0.5) return "review";
  return "review";
}

function subscriptionStatusChipColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "trialing") return "info";
  if (s === "past_due") return "warning";
  if (s === "suspended" || s === "cancelled") return "error";
  return "default";
}

/** Rótulo da seção (slug da URL) — usado no breadcrumb e nas tabs dinâmicas. */
function workspaceDetailSectionLabel(sectionSlug, t) {
  const s = String(sectionSlug || "overview")
    .trim()
    .toLowerCase();
  switch (s) {
    case "overview":
      return t("platformPage.detailTabOverview", { defaultValue: "Resumo" });
    case "health":
      return t("platformPage.detailTabHealth", { defaultValue: "Saúde (360)" });
    case "usage":
      return t("platformPage.detailTabUsage", { defaultValue: "Uso" });
    case "invoices":
      return t("platformPage.detailTabInvoices", { defaultValue: "Faturas" });
    case "offers":
      return t("platformPage.detailTabOffers", { defaultValue: "Ofertas" });
    case "segment":
      return t("platformPage.detailTabSegment");
    case "plan":
      return t("platformPage.detailTabPlan", { defaultValue: "Plano" });
    case "audit":
      return t("platformPage.detailTabAudit", { defaultValue: "Auditoria" });
    case "superlogica":
      return t("platformPage.detailTabSuperlogica", { defaultValue: "Superlógica" });
    case "passenger-sales":
      return t("platformPage.detailTabPassengerSales", { defaultValue: "Passagens" });
    case "gupshup-reconcile":
      return t("platformPage.detailTabGupshupReconcile", { defaultValue: "Gupshup" });
    default:
      return t("platformPage.detailTabOverview", { defaultValue: "Resumo" });
  }
}

/** Título de subsecção no card “Cadastro / tenant” — ocupa a linha inteira da grade. */
const TENANT_METRICS_SECTION_TITLE_SX = {
  gridColumn: "1 / -1",
  mt: 2.5,
  mb: 0,
  pb: 1,
  fontSize: "0.8125rem",
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "text.primary",
  borderBottom: "1px solid",
  borderColor: "divider",
};

/** Métrica do bloco “Cadastro / tenant”: ícone + rótulo + valor (sem linhas internas espúrias). */
function TenantMetricTile({ icon: Icon, label, labelTooltip, children }) {
  const labelTypography = (
    <Typography
      variant="caption"
      color="text.secondary"
      component="div"
      sx={{
        mb: 0.35,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        fontSize: "0.65rem",
        lineHeight: 1.35,
        wordBreak: "break-word",
        hyphens: "auto",
      }}
    >
      {label}
    </Typography>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        height: "100%",
        width: "100%",
        borderRadius: 2,
        borderColor: "divider",
        bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.06 : 0.035),
        transition: (th) =>
          th.transitions.create(["box-shadow", "border-color"], { duration: th.transitions.duration.shorter }),
        "&:hover": {
          borderColor: "primary.light",
          boxShadow: (th) => `0 2px 10px ${alpha(th.palette.common.black, th.palette.mode === "dark" ? 0.28 : 0.07)}`,
        },
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (th) => alpha(th.palette.primary.main, 0.14),
          }}
        >
          {Icon ? <Icon sx={{ fontSize: "1.05rem" }} color="primary" /> : null}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {labelTooltip ? (
            <Tooltip title={labelTooltip} arrow placement="top" enterTouchDelay={400}>
              <Box component="span" sx={{ display: "block", cursor: "help" }}>
                {labelTypography}
              </Box>
            </Tooltip>
          ) : (
            labelTypography
          )}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.4,
              wordBreak: "break-word",
              fontSize: "0.98rem",
            }}
          >
            {children}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

/** Tendência compacta no tile de interações (alinhada à tabela “Tendência de consumo”). */
function KpiPanoramaDelta({ pct, t }) {
  const n = pct == null || Number.isNaN(Number(pct)) ? null : Number(pct);
  if (n == null) return null;
  const tone = n === 0 ? "flat" : n > 0 ? "up" : "down";
  const colorKey = tone === "up" ? "success.main" : tone === "down" ? "error.main" : "text.secondary";
  const signed = n > 0 ? `+${n}%` : `${n}%`;

  return (
    <Stack
      direction="row"
      spacing={0.35}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: "divider" }}
    >
      {tone === "up" ? <ArrowUpwardIcon sx={{ fontSize: "0.9rem", color: colorKey }} aria-hidden /> : null}
      {tone === "down" ? <ArrowDownwardIcon sx={{ fontSize: "0.9rem", color: colorKey }} aria-hidden /> : null}
      <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: colorKey, fontVariantNumeric: "tabular-nums" }}>
        {signed}
      </Typography>
      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400, lineHeight: 1.4 }}>
        {t("platformPage.overviewKpiTrendVsPrior")}
      </Typography>
    </Stack>
  );
}

/** Bloco compacto para KPIs do panorama (Resumo). */
function OverviewStatTile({ label, children, secondary, trend }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.75, sm: 2 },
        height: "100%",
        minHeight: 96,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        minWidth: 0,
        borderColor: "divider",
        bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.05 : 0.03),
        borderLeft: "3px solid",
        borderLeftColor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.55 : 0.42),
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 0.75, fontWeight: 600, letterSpacing: 0.06, textTransform: "uppercase", fontSize: "0.68rem" }}
      >
        {label}
      </Typography>
      <Box sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: "break-word", fontSize: "1.35rem", fontVariantNumeric: "tabular-nums" }}>
        {children}
      </Box>
      {trend}
      {secondary ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.35, wordBreak: "break-word" }}>
          {secondary}
        </Typography>
      ) : null}
    </Paper>
  );
}

/** Δ % na tabela de tendência: cores semânticas discretas + setas (leitura em escaneamento). */
function PeriodCompareDeltaCell({ row, t }) {
  const pct = row.pct_change == null || Number.isNaN(Number(row.pct_change)) ? null : Number(row.pct_change);
  const tone = pct == null ? "none" : pct === 0 ? "flat" : pct > 0 ? "up" : "down";
  const colorKey = tone === "up" ? "success.main" : tone === "down" ? "error.main" : "text.secondary";
  const label =
    pct == null
      ? t("platformPage.usageDeltaUnavailable")
      : t("platformPage.usageDeltaPctLabel", { pct });

  return (
    <TableCell align="right" sx={{ verticalAlign: "middle", width: "26%" }} aria-label={label}>
      <Stack spacing={0.5} alignItems="flex-end" justifyContent="center" sx={{ py: 0.15 }}>
        <Stack direction="row" spacing={0.35} alignItems="center" justifyContent="flex-end" sx={{ color: colorKey }}>
          {tone === "up" ? (
            <ArrowUpwardIcon sx={{ fontSize: "1rem", flexShrink: 0 }} aria-hidden />
          ) : null}
          {tone === "down" ? (
            <ArrowDownwardIcon sx={{ fontSize: "1rem", flexShrink: 0 }} aria-hidden />
          ) : null}
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              color: colorKey,
              lineHeight: 1.35,
            }}
          >
            {pct == null ? "—" : `${pct}%`}
          </Typography>
        </Stack>
        {row.usage_trend_down ? (
          <Chip
            size="small"
            variant="outlined"
            color="error"
            label={t("platformPage.badge.usage_trend_down")}
            sx={{
              height: 22,
              maxWidth: "100%",
              "& .MuiChip-label": { px: 0.85, py: 0, fontSize: "0.65rem", lineHeight: 1.2 },
            }}
          />
        ) : null}
      </Stack>
    </TableCell>
  );
}

/** Cartões/papers de seção — raio e sombra (borda vem de variant="outlined") */
const WS_SURFACE_SX = {
  borderRadius: 2,
  boxShadow: (th) => `0 1px 3px ${alpha(th.palette.common.black, th.palette.mode === "dark" ? 0.26 : 0.07)}`,
};

/** Compensa `CardContent` `p: 2.5` para o `Divider` ir de borda a borda do Card. */
const WS_OVERVIEW_SECTION_DIVIDER_SX = {
  mx: -2.5,
  mb: 2,
};

/** Container de tabela com cantos alinhados ao card */
const WS_TABLE_CONTAINER_SX = {
  borderRadius: 1.5,
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
};

/** Cabeçalho zebra/hover nas tabelas densas do workspace */
const WS_TABLE_DENSE_SX = {
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    color: "text.secondary",
    bgcolor: (th) =>
      th.palette.mode === "dark" ? alpha(th.palette.common.white, 0.06) : alpha(th.palette.primary.main, 0.07),
    borderBottom: "2px solid",
    borderColor: "divider",
    py: 1,
    lineHeight: 1.25,
  },
  "& .MuiTableCell-body": {
    py: 1,
    verticalAlign: "middle",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    bgcolor: "action.hover",
  },
};

/** Resumo — “Tendência de consumo”: padding generoso e calha entre colunas numéricas adjacentes. */
const WS_USAGE_TREND_TABLE_SX = {
  ...WS_TABLE_DENSE_SX,
  tableLayout: "fixed",
  "& .MuiTableCell-head": {
    ...WS_TABLE_DENSE_SX["& .MuiTableCell-head"],
    py: 1.35,
    px: { xs: 1.75, sm: 2.25 },
  },
  "& .MuiTableCell-body": {
    ...WS_TABLE_DENSE_SX["& .MuiTableCell-body"],
    py: 1.45,
    verticalAlign: "middle",
  },
  "& .MuiTableHead-root .MuiTableCell-root:nth-of-type(2), & .MuiTableBody-root .MuiTableCell-root:nth-of-type(2)": {
    pr: { xs: 2, sm: 2.75 },
  },
  "& .MuiTableHead-root .MuiTableCell-root:nth-of-type(3), & .MuiTableBody-root .MuiTableCell-root:nth-of-type(3)": {
    pl: { xs: 2, sm: 2.75 },
  },
};

const WORKSPACE_SIGNAL_ALERT_SX = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  height: "100%",
  py: 1.25,
  alignItems: "flex-start",
  borderRadius: 2,
  "& .MuiAlert-message": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    py: 0,
    overflowWrap: "anywhere",
  },
};

const WorkspaceDetailPage = () => {
  const { dominioId: dominioIdParam, section: sectionParam } = useParams();
  const navigate = useNavigate();
  const outletCtx = useOutletContext();
  const registerRefresh = outletCtx?.registerRefresh;
  const outletStopRefreshing = outletCtx?.stopRefreshing;
  const { t, i18n } = useTranslation("app");
  const { can } = usePlatformConsoleAcl();
  const canGovernanceView = can("governance", "view");
  const canCatalogView = can("catalog", "view");
  const canSegmentsView = can("segments", "view");
  const dominioId = String(dominioIdParam || "").trim();
  const locale = i18n?.language || undefined;

  const innerTab = workspaceDetailSectionToIndex(sectionParam);

  const workspaceSectionTitle = useMemo(
    () => workspaceDetailSectionLabel(sectionParam, t),
    [sectionParam, t]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [detail, setDetail] = useState(null);

  const [usageData, setUsageData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [offerAnalytics, setOfferAnalytics] = useState([]);
  const [audit, setAudit] = useState([]);
  const [healthSnap, setHealthSnap] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");

  const [slLoading, setSlLoading] = useState(false);
  const [slError, setSlError] = useState("");
  const [slLink, setSlLink] = useState(null);
  const [slSummary, setSlSummary] = useState(null);
  const [slDoc, setSlDoc] = useState("");
  const [slCandidates, setSlCandidates] = useState([]);
  const [slPickOpen, setSlPickOpen] = useState(false);
  const [slPickId, setSlPickId] = useState("");
  const [slFinance, setSlFinance] = useState(null);
  const [slFinanceLoading, setSlFinanceLoading] = useState(false);
  const [slFinanceError, setSlFinanceError] = useState("");

  const [segmentValue, setSegmentValue] = useState(WorkspaceSegments.Generic);
  const [segmentSaving, setSegmentSaving] = useState(false);
  const [segmentCatalogItems, setSegmentCatalogItems] = useState([]);

  const [aiCtxLoading, setAiCtxLoading] = useState(false);
  const [aiCtxSaving, setAiCtxSaving] = useState(false);
  const [aiCtxForm, setAiCtxForm] = useState({
    companyName: "",
    companyWebsite: "",
    companyAbout: "",
    segmento: "",
    segmentKey: "",
    publicoAlvo: "",
    focoPrincipal: "",
    maturidadeDigital: "",
    capacidadeEstrategica: "",
    _updatedAt: "",
    _updatedByUserId: "",
  });

  const [planCatalog, setPlanCatalog] = useState([]);
  const [resellerList, setResellerList] = useState([]);
  const [assignForm, setAssignForm] = useState({
    planSku: "",
    version: 1,
    billingMode: "postpaid",
    enforceLimits: false,
    status: "active",
    resellerId: "",
  });
  const [assigning, setAssigning] = useState(false);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excluding, setExcluding] = useState(false);
  const [syncLoginLoading, setSyncLoginLoading] = useState(false);
  /** Overview: timestamps completos do período — secundário, expandido sob demanda. */
  const [subscriptionPreciseOpen, setSubscriptionPreciseOpen] = useState(false);

  const [passengerSalesPayload, setPassengerSalesPayload] = useState(null);
  const [passengerSalesLoading, setPassengerSalesLoading] = useState(false);
  const [passengerSalesError, setPassengerSalesError] = useState("");

  const [gupshupPayload, setGupshupPayload] = useState(null);
  const [gupshupLoading, setGupshupLoading] = useState(false);
  const [gupshupError, setGupshupError] = useState("");

  const billingModeLabel = useCallback(
    (mode) =>
      t(`billingPage.billingMode.${String(mode || "").toLowerCase()}`, {
        defaultValue: String(mode || "-"),
      }),
    [t]
  );

  const subscriptionStatusLabel = useCallback(
    (status) =>
      t(`billingPage.subscriptionStatus.${String(status || "").toLowerCase()}`, {
        defaultValue: String(status || "-"),
      }),
    [t]
  );

  const metricLabel = useCallback(
    (key) =>
      t(`billingPage.metricKeys.${key}`, {
        defaultValue: String(key || "").replace(/_/g, " "),
      }),
    [t]
  );

  const segmentSelectOptions = useMemo(() => {
    const base =
      segmentCatalogItems.length > 0
        ? segmentCatalogItems
        : ALL_WORKSPACE_SEGMENTS.map((seg) => ({
            segmentKey: seg,
            displayName: t(`platformPage.segmentOption.${seg}`, { defaultValue: seg }),
          }));
    const keys = new Set(base.map((r) => r.segmentKey));
    const v = String(segmentValue || "").trim();
    if (v && !keys.has(v)) {
      return [{ segmentKey: v, displayName: v }, ...base];
    }
    return base;
  }, [segmentCatalogItems, t, segmentValue]);

  const overviewSegmentLabel = useMemo(() => {
    const v = String(segmentValue || "").trim();
    const opt = segmentSelectOptions.find((o) => String(o.segmentKey) === v);
    return opt?.displayName || (v ? t(`platformPage.segmentOption.${v}`, { defaultValue: v }) : "—");
  }, [segmentSelectOptions, segmentValue, t]);

  const showPassengerSalesTab = segmentValue === WorkspaceSegments.PassengerTransport;

  const visibleWorkspaceSections = useMemo(
    () =>
      WORKSPACE_DETAIL_SECTION_SLUGS.filter(
        (slug) => slug !== "passenger-sales" || showPassengerSalesTab
      ),
    [showPassengerSalesTab]
  );

  const workspaceTabUiIndex = useMemo(() => {
    const s = String(sectionParam || "overview").trim().toLowerCase();
    const i = visibleWorkspaceSections.indexOf(s);
    return i >= 0 ? i : 0;
  }, [sectionParam, visibleWorkspaceSections]);

  const overviewRecentInvoice = useMemo(() => {
    if (!Array.isArray(invoices) || invoices.length === 0) return null;
    const sorted = [...invoices].sort((a, b) => {
      const ta = new Date(a.due_at || a.created_at || a.issued_at || 0).getTime();
      const tb = new Date(b.due_at || b.created_at || b.issued_at || 0).getTime();
      return tb - ta;
    });
    return sorted[0];
  }, [invoices]);

  const loadDetailBundle = useCallback(async () => {
    if (!dominioId) {
      outletStopRefreshing?.();
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [dRes, usageRes, invRes, offRes, audRes, catRes, segRes, catalogSegRes, resellersRes] = await Promise.all([
        getPlatformDominioAdminDetail(dominioId),
        getPlatformDominioUsageSummary(dominioId),
        listPlatformDominioInvoices(dominioId, { limit: 50 }),
        getPlatformDominioOfferAnalytics(dominioId),
        listPlatformBillingAudit(dominioId, { limit: 40 }).catch(() => ({ items: [] })),
        getBillingPlanCatalog().catch(() => ({ items: [] })),
        getPlatformDominioWorkspaceSegment(dominioId).catch(() => null),
        listPlatformWorkspaceSegmentCatalog({
          locale: i18n.resolvedLanguage || i18n.language || "pt-BR",
        }).catch(() => ({ items: [] })),
        listAdminResellers().catch(() => ({ items: [] })),
      ]);
      setDetail(dRes);
      setUsageData(usageRes);
      setInvoices(Array.isArray(invRes?.items) ? invRes.items : []);
      setOfferAnalytics(Array.isArray(offRes?.items) ? offRes.items : []);
      setAudit(Array.isArray(audRes?.items) ? audRes.items : []);
      setPlanCatalog(Array.isArray(catRes?.items) ? catRes.items : []);
      setSegmentValue(segRes?.segment || WorkspaceSegments.Generic);
      setSegmentCatalogItems(Array.isArray(catalogSegRes?.items) ? catalogSegRes.items : []);
      setResellerList(Array.isArray(resellersRes?.items) ? resellersRes.items : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("platformPage.loadError"));
    } finally {
      setLoading(false);
      outletStopRefreshing?.();
    }
  }, [dominioId, t, i18n.resolvedLanguage, i18n.language, outletStopRefreshing]);

  useEffect(() => {
    loadDetailBundle();
  }, [loadDetailBundle]);

  useEffect(() => {
    if (!registerRefresh) return undefined;
    registerRefresh(() => loadDetailBundle());
    return () => registerRefresh(() => outletStopRefreshing?.());
  }, [registerRefresh, loadDetailBundle, outletStopRefreshing]);

  useEffect(() => {
    if (!dominioId) return;
    if (!sectionParam) return;
    const sl = String(sectionParam).trim().toLowerCase();
    if (!WORKSPACE_DETAIL_SECTION_SLUGS.includes(sl)) {
      navigate(workspaceDetailPath(dominioId, "overview"), { replace: true });
    }
  }, [sectionParam, dominioId, navigate]);

  useEffect(() => {
    if (loading) return;
    const sl = String(sectionParam || "").trim().toLowerCase();
    if (sl !== "passenger-sales") return;
    if (!showPassengerSalesTab) {
      navigate(workspaceDetailPath(dominioId, "overview"), { replace: true });
    }
  }, [loading, sectionParam, showPassengerSalesTab, dominioId, navigate]);

  const handleWorkspaceSectionTabChange = useCallback(
    (_, newIndex) => {
      const slug = visibleWorkspaceSections[newIndex];
      if (slug) navigate(workspaceDetailPath(dominioId, slug), { replace: true });
    },
    [navigate, dominioId, visibleWorkspaceSections]
  );

  const selectablePlans = useMemo(() => {
    const set = new Set();
    const out = [];
    for (const row of planCatalog) {
      if (!row?.sku || row?.version == null) continue;
      const key = `${row.sku}:${row.version}`;
      if (set.has(key)) continue;
      set.add(key);
      out.push({
        sku: row.sku,
        version: Number(row.version),
        label: `${row.name || row.sku} (v${row.version})`,
      });
    }
    return out;
  }, [planCatalog]);

  useEffect(() => {
    if (!selectablePlans.length) return;
    const hasPlan = selectablePlans.some(
      (p) => p.sku === assignForm.planSku && Number(p.version) === Number(assignForm.version)
    );
    if (!hasPlan) {
      setAssignForm((prev) => ({
        ...prev,
        planSku: selectablePlans[0].sku,
        version: selectablePlans[0].version,
      }));
    }
  }, [selectablePlans, assignForm.planSku, assignForm.version]);

  useEffect(() => {
    const s = detail?.subscription;
    if (!s?.plan_sku) return;
    setAssignForm((prev) => ({
      ...prev,
      planSku: s.plan_sku || prev.planSku,
      version: Number(s.plan_version) || prev.version,
      billingMode: s.billing_mode || prev.billingMode,
      enforceLimits: !!Number(s.enforce_limits),
      status: s.subscription_status || prev.status,
      resellerId:
        s.reseller_id != null && String(s.reseller_id).trim() !== ""
          ? String(s.reseller_id)
          : "",
    }));
  }, [
    detail?.subscription?.subscription_id,
    detail?.subscription?.reseller_id,
    detail?.subscription?.plan_sku,
    detail?.subscription?.plan_version,
  ]);

  const handleSaveSegment = async () => {
    setSegmentSaving(true);
    setError("");
    setSuccess("");
    try {
      await setPlatformDominioWorkspaceSegment(dominioId, segmentValue);
      setSuccess(t("platformPage.segmentSaved"));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("platformPage.segmentSaveError"));
    } finally {
      setSegmentSaving(false);
    }
  };

  useEffect(() => {
    setSubscriptionPreciseOpen(false);
  }, [dominioId]);

  useEffect(() => {
    if (innerTab !== 5 || !dominioId) return undefined;
    let cancelled = false;
    setAiCtxLoading(true);
    setError("");
    getPlatformDominioAiWorkspaceContext(dominioId)
      .then((res) => {
        if (cancelled) return;
        const c = res?.aiContext && typeof res.aiContext === "object" ? res.aiContext : {};
        setAiCtxForm({
          companyName: String(c.companyName || ""),
          companyWebsite: String(c.companyWebsite || ""),
          companyAbout: String(c.companyAbout || ""),
          segmento: String(c.segmento || ""),
          segmentKey: String(c.segmentKey || ""),
          publicoAlvo: String(c.publicoAlvo || ""),
          focoPrincipal: String(c.focoPrincipal || ""),
          maturidadeDigital: String(c.maturidadeDigital || ""),
          capacidadeEstrategica: String(c.capacidadeEstrategica || ""),
          _updatedAt: c.updatedAt ? String(c.updatedAt) : "",
          _updatedByUserId: c.updatedByUserId != null ? String(c.updatedByUserId) : "",
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || t("platformPage.aiContextLoadError"));
        }
      })
      .finally(() => {
        if (!cancelled) setAiCtxLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [innerTab, dominioId, t]);

  const handleSaveAiWorkspaceContext = async () => {
    setAiCtxSaving(true);
    setError("");
    setSuccess("");
    try {
      const { _updatedAt, _updatedByUserId, ...patch } = aiCtxForm;
      await setPlatformDominioAiWorkspaceContext(dominioId, { ...patch, source: "manual" });
      setSuccess(t("platformPage.aiContextSaved"));
      const res = await getPlatformDominioAiWorkspaceContext(dominioId);
      const c = res?.aiContext && typeof res.aiContext === "object" ? res.aiContext : {};
      setAiCtxForm((prev) => ({
        ...prev,
        _updatedAt: c.updatedAt ? String(c.updatedAt) : "",
        _updatedByUserId: c.updatedByUserId != null ? String(c.updatedByUserId) : "",
      }));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("platformPage.aiContextSaveError"));
    } finally {
      setAiCtxSaving(false);
    }
  };

  const handleConfirmExclude = async () => {
    const did = Number(dominioId);
    if (!Number.isFinite(did) || did <= 0) return;
    setExcluding(true);
    setError("");
    setSuccess("");
    try {
      await excludePlatformDominio(did);
      clearStoredWorkspaceIfExcluded(detail?.dominio?.dominio, detail?.dominio?.bd);
      setExcludeDialogOpen(false);
      navigate(PLATFORM_CONSOLE_PATH.workspaces);
    } catch (err) {
      setExcludeDialogOpen(false);
      setError(err?.response?.data?.message || err?.message || t("platformPage.excludeWorkspaceError"));
    } finally {
      setExcluding(false);
    }
  };

  const handleSyncTenantLogin = async () => {
    const did = Number(dominioId);
    if (!Number.isFinite(did) || did <= 0) return;
    setSyncLoginLoading(true);
    setError("");
    setSuccess("");
    try {
      await postPlatformDominioSyncTenantLogin(did);
      setSuccess(t("platformPage.syncTenantLoginSuccess"));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("platformPage.syncTenantLoginError"));
    } finally {
      setSyncLoginLoading(false);
    }
  };

  const handleAssign = async () => {
    const did = Number(dominioId);
    if (!Number.isFinite(did) || did <= 0) return;
    setAssigning(true);
    setError("");
    setSuccess("");
    try {
      await assignBillingSubscription({
        dominioId: did,
        planSku: assignForm.planSku,
        version: Number(assignForm.version),
        billingMode: assignForm.billingMode,
        enforceLimits: !!assignForm.enforceLimits,
        status: assignForm.status,
        resellerId:
          assignForm.resellerId !== "" && Number.isFinite(Number(assignForm.resellerId))
            ? Number(assignForm.resellerId)
            : null,
      });
      setSuccess(t("billingPage.adminMsgAssignOk"));
      await loadDetailBundle();
    } catch (err) {
      setError(err?.message || "Falha ao atualizar assinatura.");
    } finally {
      setAssigning(false);
    }
  };

  const sub = detail?.subscription;
  const dominioRow = detail?.dominio;
  const signals = detail?.signals;
  const tenantLegacy = detail?.tenant_legacy;
  const commercialKpis = detail?.commercial_kpis;

  /** Coerência com API nova (`churn_risk_hint`); fallback alinha com backend quando campo ausente. */
  const churnRiskHint = useMemo(() => {
    if (signals == null) return false;
    if (typeof signals.churn_risk_hint === "boolean") return signals.churn_risk_hint;
    const ix = (signals.period_compare || []).find((x) => x.metric_key === "interactions");
    if (ix) return ix.usage_trend_down === true;
    return (signals.period_compare || []).some((r) => r.usage_trend_down);
  }, [signals]);

  const workspaceSignalAlertCount = useMemo(() => {
    let n = 0;
    if (signals?.near_plan_limit?.length > 0) n += 1;
    if (churnRiskHint) n += 1;
    if (signals?.expansion_opportunity_hint) n += 1;
    return n;
  }, [signals, churnRiskHint]);

  const workspaceAlertGridMd = workspaceSignalAlertCount <= 1 ? 12 : workspaceSignalAlertCount === 2 ? 6 : 4;

  const interactionsCompareRow = useMemo(
    () => (signals?.period_compare || []).find((x) => x.metric_key === "interactions") ?? null,
    [signals?.period_compare]
  );

  const usageCompareRows = signals?.period_compare ?? [];

  const loadHealthSnapshot = useCallback(async () => {
    if (!dominioId) return;
    setHealthLoading(true);
    setHealthError("");
    try {
      // 1) Carrega rápido (KPIs + tempos). Sem snapshot noturno → live=1 consulta o tenant na hora.
      const fastParams = { days: 30, mode: "fast", include: "summary,time" };
      let base = await getPlatformDominioHealthSnapshot(dominioId, fastParams);
      let liveFlag = {};
      if (base?.code === "snapshot_missing") {
        liveFlag = { live: "1" };
        base = await getPlatformDominioHealthSnapshot(dominioId, { ...fastParams, ...liveFlag });
      }
      setHealthSnap(base);

      // 2) Carrega detalhes em background (tabelas/sections). Cache curto no backend evita recomputar.
      const loadDetails = async () => {
        const parts = await Promise.allSettled([
          getPlatformDominioHealthSnapshot(dominioId, { days: 30, mode: "fast", include: "channels", ...liveFlag }),
          getPlatformDominioHealthSnapshot(dominioId, { days: 30, mode: "fast", include: "operators", ...liveFlag }),
          getPlatformDominioHealthSnapshot(dominioId, { days: 30, mode: "fast", include: "bot_proxy", ...liveFlag }),
          // volume em full para permitir "ever" quando o banco aguentar (ou ficar em cache).
          getPlatformDominioHealthSnapshot(dominioId, { days: 30, mode: "full", include: "volume", ...liveFlag }),
        ]);

        const merge = (prev, next) => {
          if (!next) return prev;
          return {
            ...(prev || {}),
            ...(next || {}),
            user_retention: next.user_retention ?? prev?.user_retention,
            attendances: next.attendances ?? prev?.attendances,
            channels: next.channels ?? prev?.channels,
            operator_messages: next.operator_messages ?? prev?.operator_messages,
            bot_proxy: next.bot_proxy ?? prev?.bot_proxy,
            time: next.time ?? prev?.time,
            volume: next.volume ?? prev?.volume,
            time_to_value: next.time_to_value ?? prev?.time_to_value,
            meta: { ...(prev?.meta || {}), ...(next?.meta || {}) },
          };
        };

        setHealthSnap((prev) => {
          let acc = prev;
          for (const r of parts) {
            if (r.status === "fulfilled") acc = merge(acc, r.value);
          }
          return acc;
        });
      };
      loadDetails().catch(() => {});
    } catch (err) {
      setHealthSnap(null);
      setHealthError(err?.response?.data?.message || err?.message || t("platformPage.healthLoadError"));
    } finally {
      setHealthLoading(false);
    }
  }, [dominioId, t]);

  useEffect(() => {
    if (innerTab === 1) loadHealthSnapshot();
  }, [innerTab, loadHealthSnapshot]);

  const loadSuperlogica = useCallback(async () => {
    if (!dominioId) return;
    setSlLoading(true);
    setSlError("");
    try {
      const [linkRes, sumRes] = await Promise.all([
        getPlatformDominioSuperlogicaLink(dominioId),
        getPlatformDominioSuperlogicaSummary(dominioId).catch(() => null),
      ]);
      setSlLink(linkRes?.link || null);
      setSlSummary(sumRes?.summary || null);
    } catch (err) {
      setSlError(err?.response?.data?.message || err?.message || "Falha ao carregar Superlógica.");
      setSlLink(null);
      setSlSummary(null);
    } finally {
      setSlLoading(false);
    }
  }, [dominioId]);

  useEffect(() => {
    if (innerTab === 6 || innerTab === 8) loadSuperlogica();
  }, [innerTab, loadSuperlogica]);

  const loadFinanceTab = useCallback(async () => {
    if (!dominioId) return;
    setSlFinanceLoading(true);
    setSlFinanceError("");
    try {
      const linkRes = await getPlatformDominioSuperlogicaLink(dominioId).catch(() => null);
      const link = linkRes?.link || null;
      if (link) setSlLink((prev) => prev || link);
      if (!link?.superlogica_cliente_id) {
        setSlFinance({ not_linked: true });
        return;
      }
      const data = await getPlatformDominioSuperlogicaCobrancas(dominioId);
      setSlFinance(data);
    } catch (err) {
      const st = err?.response?.status;
      if (st === 409) {
        setSlFinance({ not_linked: true });
      } else {
        setSlFinanceError(err?.response?.data?.message || err?.message || "");
        setSlFinance(null);
      }
    } finally {
      setSlFinanceLoading(false);
    }
  }, [dominioId]);

  useEffect(() => {
    if (innerTab === 3) loadFinanceTab();
  }, [innerTab, loadFinanceTab]);

  const loadPassengerSalesTab = useCallback(async () => {
    if (!dominioId) return;
    setPassengerSalesLoading(true);
    setPassengerSalesError("");
    try {
      const data = await getPlatformDominioPassengerSales(dominioId);
      setPassengerSalesPayload(data);
    } catch (err) {
      setPassengerSalesPayload(null);
      setPassengerSalesError(
        err?.response?.data?.message || err?.message || t("platformPage.passengerSalesLoadError")
      );
    } finally {
      setPassengerSalesLoading(false);
    }
  }, [dominioId, t]);

  useEffect(() => {
    if (innerTab === 9) loadPassengerSalesTab();
  }, [innerTab, loadPassengerSalesTab]);

  const loadGupshupReconcileTab = useCallback(async () => {
    if (!dominioId) return;
    setGupshupLoading(true);
    setGupshupError("");
    setGupshupPayload(null);
    try {
      const data = await getPlatformDominioGupshupReconciliation(dominioId);
      setGupshupPayload(data ?? {});
    } catch (err) {
      setGupshupPayload(null);
      setGupshupError(err?.response?.data?.message || err?.message || t("platformPage.gupshupReconcileLoadError"));
    } finally {
      setGupshupLoading(false);
    }
  }, [dominioId, t]);

  useEffect(() => {
    if (innerTab === 10) loadGupshupReconcileTab();
  }, [innerTab, loadGupshupReconcileTab]);

  useEffect(() => {
    setSlFinance(null);
    setSlFinanceError("");
  }, [dominioId]);

  useEffect(() => {
    setPassengerSalesPayload(null);
    setPassengerSalesError("");
    setGupshupPayload(null);
    setGupshupError("");
  }, [dominioId]);

  const normalizeDocPreview = (v) => String(v || "").replace(/[^\d./-]/g, "").trim();

  const doLinkByDoc = async () => {
    if (!dominioId) return;
    setSlLoading(true);
    setSlError("");
    try {
      const res = await postPlatformDominioSuperlogicaLink(dominioId, { docValue: slDoc });
      setSlCandidates([]);
      setSlPickId("");
      setSlPickOpen(false);
      await loadSuperlogica();
      await loadFinanceTab();
      return res;
    } catch (err) {
      const status = err?.response?.status;
      const payload = err?.response?.data?.data;
      if (status === 409 && payload?.code === "multiple_matches" && Array.isArray(payload?.candidates)) {
        setSlCandidates(payload.candidates);
        setSlPickOpen(true);
        setSlPickId(payload.candidates?.[0]?.id ? String(payload.candidates[0].id) : "");
        setSlError(t("platformPage.superlogicaMultiMatch", { defaultValue: "Mais de um cliente encontrado. Selecione o correto." }));
      } else {
        setSlError(err?.response?.data?.message || err?.message || "Falha ao vincular Superlógica.");
      }
    } finally {
      setSlLoading(false);
    }
  };

  const doForcePickCandidate = async () => {
    if (!dominioId) return;
    if (!slPickId) return;
    setSlLoading(true);
    setSlError("");
    try {
      await postPlatformDominioSuperlogicaLink(dominioId, {
        superlogicaClienteId: slPickId,
        docValue: slDoc || null,
      });
      setSlPickOpen(false);
      setSlCandidates([]);
      await loadSuperlogica();
      await loadFinanceTab();
    } catch (err) {
      setSlError(err?.response?.data?.message || err?.message || "Falha ao vincular Superlógica.");
    } finally {
      setSlLoading(false);
    }
  };

  const doUnlink = async () => {
    if (!dominioId) return;
    setSlLoading(true);
    setSlError("");
    try {
      await deletePlatformDominioSuperlogicaLink(dominioId);
      setSlLink(null);
      setSlSummary(null);
      setSlFinance(null);
      setSlCandidates([]);
      setSlPickOpen(false);
    } catch (err) {
      setSlError(err?.response?.data?.message || err?.message || "Falha ao desvincular Superlógica.");
    } finally {
      setSlLoading(false);
    }
  };

  if (!dominioId) {
    return (
      <Typography color="error">{t("platformPage.pickDomain")}</Typography>
    );
  }

  const host = dominioRow?.dominio || "";
  const bdRegistered = dominioRow?.bd != null && String(dominioRow.bd).trim() !== "";
  const schemaInferred = bdRegistered ? "" : inferSchemaFromHost(host);
  const schemaDisplay = bdRegistered ? String(dominioRow.bd).trim() : schemaInferred || "";
  const tenantDbSlug = resolveTenantDatabaseName(dominioRow?.dominio, dominioRow?.bd);
  const canOpenInApp = Number(dominioRow?.excluido) === 0 && Boolean(String(tenantDbSlug || "").trim());

  const listLink = PLATFORM_CONSOLE_PATH.workspaces;

  return (
    <Box sx={{ pt: 1, pb: 4, width: 1, minWidth: 0 }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to={listLink}
          startIcon={<ArrowBackIcon />}
          size="small"
          color="inherit"
          sx={{ alignSelf: "flex-start" }}
        >
          {t("platformPage.backToWorkspaceList")}
        </Button>
        <Breadcrumbs separator="›" aria-label={t("platformPage.workspaceDetailBreadcrumbAria")}>
          <Link component={RouterLink} to={listLink} underline="hover" color="inherit" variant="body2">
            {t("platformPage.title")}
          </Link>
          <Link component={RouterLink} to={listLink} underline="hover" color="inherit" variant="body2">
            {t("platformPage.breadcrumbWorkspaceList")}
          </Link>
          <Typography color="text.primary" variant="body2">
            {host || `ID ${dominioId}`}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {workspaceSectionTitle}
          </Typography>
        </Breadcrumbs>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      ) : null}

      <Dialog open={excludeDialogOpen} onClose={() => !excluding && setExcludeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("platformPage.excludeWorkspaceDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("platformPage.excludeWorkspaceDialogBody", {
              host: dominioRow?.dominio || "—",
              id: dominioRow?.id ?? dominioId,
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExcludeDialogOpen(false)} disabled={excluding} color="inherit">
            {t("platformPage.excludeWorkspaceCancel")}
          </Button>
          <Button onClick={handleConfirmExclude} disabled={excluding} color="error" variant="contained">
            {excluding ? t("platformPage.excludeWorkspaceRunning") : t("platformPage.excludeWorkspaceConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && !detail ? (
        <Box sx={{ py: 8, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={36} />
            <Typography color="text.secondary">{t("platformPage.detailLoading")}</Typography>
          </Stack>
        </Box>
      ) : (
        <>
          {(String(dominioRow?.workspace_kind || "").toLowerCase() === "presentation" || detail?.presentation) ? (
            <>
              {!canCatalogView && (!Array.isArray(planCatalog) || planCatalog.length === 0) ? (
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                  {t("platformPage.workspaceOptionalCatalogHint")}
                </Alert>
              ) : null}
              <PresentationWorkspaceBanner
              dominioId={dominioId}
              presentation={detail?.presentation}
              planItems={planCatalog}
              getPresentationStatus={getPlatformWorkspacePresentationStatus}
              convertPresentation={convertPlatformWorkspacePresentation}
              discardPresentation={discardPlatformWorkspacePresentation}
              onChange={loadDetailBundle}
              onDiscarded={() => {
                clearStoredWorkspaceIfExcluded(dominioRow?.dominio, dominioRow?.bd);
                navigate(listLink);
              }}
            />
            </>
          ) : null}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              background: (th) =>
                `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.07)} 0%, ${alpha(th.palette.primary.main, 0.02)} 55%, ${th.palette.background.paper} 100%)`,
            }}
          >
            <Stack spacing={1.25}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "flex-start" }}
                justifyContent="space-between"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                    {t("platformPage.workspaceHeroKicker")}
                  </Typography>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, wordBreak: "break-word", lineHeight: 1.2 }}>
                    {dominioRow?.dominio || `ID ${dominioId}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, mt: 0.5 }}>
                    {t("platformPage.workspaceHeroCaption")}
                  </Typography>
                </Box>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  disabled={!canOpenInApp}
                  onClick={() => openWorkspaceInApp(navigate, dominioRow?.bd, dominioRow?.dominio)}
                  sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", alignSelf: { xs: "stretch", sm: "flex-start" } }}
                >
                  {t("platformPage.partnerOpenInApp", { defaultValue: "Abrir no atendimento" })}
                </Button>
              </Stack>
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {sub?.subscription_status ? (
                  <Chip
                    label={subscriptionStatusLabel(sub.subscription_status)}
                    color={subscriptionStatusChipColor(sub.subscription_status)}
                    size="small"
                  />
                ) : null}
                {sub?.plan_sku ? (
                  <Chip
                    icon={<SubscriptionsOutlinedIcon />}
                    label={`${sub.plan_name || sub.plan_sku} · v${sub.plan_version}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ) : (
                  <Chip label={t("platformPage.noActiveSubscription")} variant="outlined" size="small" />
                )}
                {sub?.reseller_id ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${t("platformPage.colReseller")}: ${sub.reseller_name || sub.reseller_code || sub.reseller_id}`}
                  />
                ) : null}
                {schemaDisplay ? (
                  <Tooltip
                    title={
                      bdRegistered
                        ? t("platformPage.schemaTooltipRegistered")
                        : t("platformPage.schemaHintInferred", { schema: schemaDisplay })
                    }
                  >
                    <Chip
                      icon={<StorageOutlinedIcon />}
                      label={
                        bdRegistered
                          ? `${t("platformPage.schemaChipRegistered")}: ${schemaDisplay}`
                          : `${t("platformPage.schemaInferred")}: ${schemaDisplay}`
                      }
                      variant={bdRegistered ? "outlined" : "filled"}
                      color={bdRegistered ? "default" : "warning"}
                      size="small"
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={t("platformPage.schemaTooltipMissing")}>
                    <Chip
                      icon={<StorageOutlinedIcon />}
                      label={t("platformPage.schemaUnknown")}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                )}
                <Chip
                  label={t("platformPage.chipInternalId", { id: dominioRow?.id ?? dominioId })}
                  size="small"
                  variant="outlined"
                  sx={{ borderStyle: "dashed" }}
                />
              </Stack>
            </Stack>
          </Paper>

          {workspaceSignalAlertCount > 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 2,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "hidden",
                bgcolor: (th) => alpha(th.palette.text.primary, 0.02),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25, fontWeight: 700, letterSpacing: 0.06 }}>
                {t("platformPage.workspaceSignalsPanelTitle")}
              </Typography>
              <Grid container spacing={1.5} sx={{ width: "100%", maxWidth: "100%", m: 0 }}>
                {signals?.near_plan_limit?.length > 0 ? (
                  <Grid item xs={12} md={workspaceAlertGridMd} sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Alert severity="warning" variant="outlined" sx={WORKSPACE_SIGNAL_ALERT_SX}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        {t("platformPage.signalNearLimit")}
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                        {signals.near_plan_limit.map((x) => (
                          <Typography component="li" variant="body2" key={x.metric_key}>
                            {metricLabel(x.metric_key)}: {x.used} / {x.limit} ({x.pct_used}%)
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  </Grid>
                ) : null}
                {churnRiskHint ? (
                  <Grid item xs={12} md={workspaceAlertGridMd} sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Alert severity="info" variant="outlined" sx={WORKSPACE_SIGNAL_ALERT_SX}>
                      <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                        {t("platformPage.signalUsageDown")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                        {t("platformPage.signalUsageDownSub")}
                      </Typography>
                    </Alert>
                  </Grid>
                ) : null}
                {signals?.expansion_opportunity_hint ? (
                  <Grid item xs={12} md={workspaceAlertGridMd} sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Alert severity="success" variant="outlined" sx={WORKSPACE_SIGNAL_ALERT_SX}>
                      <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                        {t("platformPage.signalExpansionHint")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                        {t("platformPage.signalExpansionHintSub")}
                      </Typography>
                    </Alert>
                  </Grid>
                ) : null}
              </Grid>
            </Paper>
          ) : null}

          <Tabs
            value={workspaceTabUiIndex}
            onChange={handleWorkspaceSectionTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 2,
              px: { xs: 0.5, sm: 1 },
              py: 0.25,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.06 : 0.04),
              "& .MuiTab-root": {
                minHeight: 46,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
              },
              "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            {visibleWorkspaceSections.map((slug) => (
              <Tab key={slug} label={workspaceDetailSectionLabel(slug, t)} />
            ))}
          </Tabs>

          {innerTab === 0 ? (
            <Stack spacing={2.5} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
              {Number(dominioRow?.excluido) !== 0 ? (
                <Alert severity="error" variant="outlined">
                  {t("platformPage.overviewExcludedBanner")}
                </Alert>
              ) : null}

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  ...WS_SURFACE_SX,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  overflow: "visible",
                }}
              >
                <Stack spacing={0} sx={{ position: "relative", isolation: "isolate" }}>
                  <Stack spacing={0.5} sx={{ mb: 2.25 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t("platformPage.overviewAtAGlance")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 720 }}>
                      {t("platformPage.overviewAtAGlanceSub")}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: { xs: 1.5, sm: 2 },
                      width: "100%",
                      minWidth: 0,
                      alignItems: "stretch",
                    }}
                  >
                    <OverviewStatTile label={t("platformPage.overviewKpiSegment")}>{overviewSegmentLabel}</OverviewStatTile>
                    <OverviewStatTile
                      label={t("platformPage.overviewKpiInteractions")}
                      trend={
                        interactionsCompareRow?.pct_change != null && !Number.isNaN(Number(interactionsCompareRow.pct_change)) ? (
                          <KpiPanoramaDelta pct={interactionsCompareRow.pct_change} t={t} />
                        ) : null
                      }
                    >
                      {tenantLegacy?.usage_interactions_30d_effective != null
                        ? Number(tenantLegacy.usage_interactions_30d_effective).toLocaleString(locale)
                        : tenantLegacy?.legacy_interactions_30d != null
                          ? Number(tenantLegacy.legacy_interactions_30d).toLocaleString(locale)
                          : "—"}
                    </OverviewStatTile>
                    <OverviewStatTile label={t("platformPage.overviewKpiLastLogin")}>
                      <Tooltip
                        disableHoverListener={!tenantLegacy?.tenant_last_user_access_at}
                        title={
                          tenantLegacy?.tenant_last_user_access_at ? fmtDate(tenantLegacy.tenant_last_user_access_at) : ""
                        }
                      >
                        <Box component="span">{fmtDateShort(tenantLegacy?.tenant_last_user_access_at, locale || "pt-BR")}</Box>
                      </Tooltip>
                    </OverviewStatTile>
                    <OverviewStatTile
                      label={t("platformPage.overviewKpiInvoicesCount")}
                      secondary={
                        overviewRecentInvoice
                          ? t("platformPage.overviewLastInvoiceLine", {
                              status: overviewRecentInvoice.status || "—",
                              amount:
                                overviewRecentInvoice.total_cents != null ? centsToBrl(overviewRecentInvoice.total_cents) : "—",
                              date: fmtDateShort(
                                overviewRecentInvoice.due_at || overviewRecentInvoice.created_at,
                                locale || "pt-BR"
                              ),
                            })
                          : t("platformPage.overviewLastInvoiceNone")
                      }
                    >
                      {invoices.length.toLocaleString(locale)}
                    </OverviewStatTile>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      mt: 5,
                      pt: 2,
                      pb: 1.5,
                      px: { xs: 1.5, sm: 2 },
                      borderRadius: 1.5,
                      bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.07 : 0.045),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.55 }}>
                      {t("platformPage.overviewPanoramaFooterHint")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {usageData?.metrics?.length ? (
                <Card variant="outlined" sx={WS_SURFACE_SX}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {t("platformPage.overviewUsageSnapshotTitle")}
                      </Typography>
                      <Button size="small" variant="outlined" component={RouterLink} to={workspaceDetailPath(dominioId, "usage")}>
                        {t("platformPage.overviewUsageSnapshotMore")}
                      </Button>
                    </Stack>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
                        gap: 1,
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      {usageData.metrics.slice(0, 6).map((m) => (
                        <Box
                          key={m.metric_key}
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: 2,
                            py: 1.25,
                            px: { xs: 1.5, sm: 2 },
                            minWidth: 0,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: (th) => alpha(th.palette.text.primary, 0.03),
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.35, flex: 1, minWidth: 0, pr: 1, overflowWrap: "anywhere" }}
                          >
                            {metricLabel(m.metric_key)}
                          </Typography>
                          <Typography variant="body1" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                            {Number(m.total_quantity).toLocaleString(locale)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ) : null}

              <Stack spacing={2.5} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 2.5,
                    width: "100%",
                    minWidth: 0,
                    alignItems: "flex-start",
                  }}
                >
                <Card
                  variant="outlined"
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    ...WS_SURFACE_SX,
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {sub ? (
                      <Stack spacing={2.25}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          flexWrap="wrap"
                          gap={1.25}
                          useFlexGap
                        >
                          <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {t("platformPage.cardSubscription")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 520, lineHeight: 1.5 }}>
                              {t("platformPage.subscriptionCardHint")}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                            {sub.subscription_status ? (
                              <Chip
                                size="small"
                                label={subscriptionStatusLabel(sub.subscription_status)}
                                color={subscriptionStatusChipColor(sub.subscription_status)}
                              />
                            ) : null}
                            {sub.plan_sku ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={`${sub.plan_name || sub.plan_sku} · v${sub.plan_version}`}
                              />
                            ) : null}
                          </Stack>
                        </Stack>

                        <Box
                          sx={{
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.07 : 0.035),
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
                              minWidth: 0,
                            }}
                          >
                            <Box sx={{ p: 2, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.75, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.68rem" }}
                              >
                                {t("platformPage.rowBillingMode")}
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                                {billingModeLabel(sub.billing_mode)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: { xs: "none", sm: "block" },
                                width: "1px",
                                alignSelf: "stretch",
                                bgcolor: "divider",
                              }}
                            />
                            <Box
                              sx={{
                                p: 2,
                                minWidth: 0,
                                borderTop: { xs: "1px solid", sm: "none" },
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.75, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.68rem" }}
                              >
                                {t("platformPage.rowEnforceLimits")}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={Number(sub.enforce_limits) ? "warning" : "default"}
                                label={
                                  Number(sub.enforce_limits)
                                    ? t("platformPage.subscriptionEnforceChipOn")
                                    : t("platformPage.subscriptionEnforceChipOff")
                                }
                                sx={{ height: 24, mb: 0.75 }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {Number(sub.enforce_limits)
                                  ? t("platformPage.rowEnforceLimitsOn")
                                  : t("platformPage.rowEnforceLimitsOff")}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: 2,
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.06 : 0.028),
                          }}
                        >
                          <Stack spacing={1.25}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} useFlexGap>
                              <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: "0.08em", lineHeight: 1.4 }}>
                                {t("platformPage.rowPeriod")}
                              </Typography>
                              {billingPeriodWholeDays(sub.current_period_start, sub.current_period_end) != null ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={t("platformPage.overviewPeriodHuman", {
                                    days: billingPeriodWholeDays(sub.current_period_start, sub.current_period_end),
                                  })}
                                />
                              ) : null}
                            </Stack>
                            <Typography variant="h6" component="div" sx={{ fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.35 }}>
                              {fmtDateShort(sub.current_period_start, locale || "pt-BR")} —{" "}
                              {fmtDateShort(sub.current_period_end, locale || "pt-BR")}
                            </Typography>
                            {billingPeriodElapsedPct(sub.current_period_start, sub.current_period_end) != null ? (
                              <Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={billingPeriodElapsedPct(sub.current_period_start, sub.current_period_end)}
                                  sx={{
                                    height: 6,
                                    borderRadius: 1,
                                    bgcolor: (th) => alpha(th.palette.primary.main, 0.14),
                                  }}
                                  aria-label={t("platformPage.subscriptionPeriodProgressAria")}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.65, lineHeight: 1.45 }}>
                                  {t("platformPage.subscriptionPeriodProgressCaption")}
                                </Typography>
                              </Box>
                            ) : null}
                            {sub.current_period_start && sub.current_period_end ? (
                              <>
                                <Button
                                  size="small"
                                  color="inherit"
                                  onClick={() => setSubscriptionPreciseOpen((o) => !o)}
                                  endIcon={
                                    <ExpandMoreIcon
                                      sx={{
                                        transition: "transform 0.2s ease",
                                        transform: subscriptionPreciseOpen ? "rotate(180deg)" : "none",
                                      }}
                                    />
                                  }
                                  sx={{
                                    alignSelf: "flex-start",
                                    mt: 0.25,
                                    px: 0,
                                    minWidth: 0,
                                    justifyContent: "flex-start",
                                    color: "text.secondary",
                                    typography: "caption",
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "transparent", color: "primary.main" },
                                  }}
                                >
                                  {subscriptionPreciseOpen
                                    ? t("platformPage.subscriptionPreciseToggleHide")
                                    : t("platformPage.subscriptionPreciseToggleShow")}
                                </Button>
                                <Collapse in={subscriptionPreciseOpen} timeout="auto" unmountOnExit>
                                  <Box
                                    sx={{
                                      mt: 0.5,
                                      p: 1.5,
                                      borderRadius: 1,
                                      bgcolor: (th) => alpha(th.palette.text.primary, th.palette.mode === "dark" ? 0.08 : 0.04),
                                      border: "1px solid",
                                      borderColor: "divider",
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
                                      {t("platformPage.subscriptionPeriodPrecise")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                                        fontSize: "0.72rem",
                                        lineHeight: 1.55,
                                        wordBreak: "break-all",
                                      }}
                                      component="div"
                                    >
                                      {fmtDate(sub.current_period_start)} — {fmtDate(sub.current_period_end)}
                                    </Typography>
                                  </Box>
                                </Collapse>
                              </>
                            ) : null}
                          </Stack>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ pt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t("platformPage.rowReseller")}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {sub.reseller_id
                              ? `${sub.reseller_name || "—"} (${sub.reseller_code || sub.reseller_id})`
                              : "—"}
                          </Typography>
                        </Stack>
                      </Stack>
                    ) : (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {t("platformPage.cardSubscription")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45 }}>
                            {t("platformPage.subscriptionCardHint")}
                          </Typography>
                        </Box>
                        <Divider sx={WS_OVERVIEW_SECTION_DIVIDER_SX} />
                        <Alert severity="info" variant="outlined">
                          {t("platformPage.noActiveSubscriptionDetail")}
                        </Alert>
                      </>
                    )}
                  </CardContent>
                </Card>
                <Card
                  variant="outlined"
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    ...WS_SURFACE_SX,
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {t("platformPage.detailSignalsTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                          {t("platformPage.usageCompareCardHint")}
                        </Typography>
                      </Box>
                      {usageCompareRows.length ? (
                        <>
                          <TableContainer
                            sx={{
                              width: 1,
                              maxWidth: "100%",
                              maxHeight: usageCompareRows.length > 8 ? 320 : undefined,
                              overflow: usageCompareRows.length > 8 ? "auto" : "visible",
                              ...WS_TABLE_CONTAINER_SX,
                              px: { xs: 0.75, sm: 1.25 },
                              py: { xs: 0.5, sm: 0.75 },
                              boxSizing: "border-box",
                            }}
                          >
                            <Table size="small" sx={WS_USAGE_TREND_TABLE_SX}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: "36%" }}>{t("platformPage.metric")}</TableCell>
                                  <TableCell align="right" sx={{ width: "19%" }}>
                                    {t("platformPage.usageCurrent")}
                                  </TableCell>
                                  <TableCell align="right" sx={{ width: "19%" }}>
                                    {t("platformPage.usagePrevious")}
                                  </TableCell>
                                  <TableCell align="right" sx={{ width: "26%" }}>
                                    Δ %
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {usageCompareRows.map((r) => (
                                  <TableRow key={r.metric_key} hover>
                                    <TableCell sx={{ wordBreak: "break-word" }}>{metricLabel(r.metric_key)}</TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                      {Number(r.current).toLocaleString(locale)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                      {Number(r.previous).toLocaleString(locale)}
                                    </TableCell>
                                    <PeriodCompareDeltaCell row={r} t={t} />
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            justifyContent="space-between"
                            sx={{
                              pt: 1.5,
                              mt: 0.5,
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, maxWidth: 420 }}>
                              {t("platformPage.usageTrendFooterHint")}
                            </Typography>
                            <Link
                              component={RouterLink}
                              to={workspaceDetailPath(dominioId, "usage")}
                              variant="body2"
                              fontWeight={600}
                              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                            >
                              {t("platformPage.overviewUsageSnapshotMore")}
                            </Link>
                          </Stack>
                        </>
                      ) : (
                        <Box
                          sx={{
                            py: 3,
                            px: 2,
                            textAlign: "center",
                            borderRadius: 2,
                            border: "1px dashed",
                            borderColor: "divider",
                            bgcolor: (th) => alpha(th.palette.primary.main, 0.04),
                          }}
                        >
                          <Box sx={{ maxWidth: 420, mx: "auto" }}>
                            <BarChartOutlinedIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              {t("platformPage.usageEmptyTitle")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t("platformPage.usageEmptyBody")}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ mt: 2 }}
                              onClick={() => navigate(workspaceDetailPath(dominioId, "usage"))}
                            >
                              {t("platformPage.usageEmptyCta")}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
                </Box>
                <Card variant="outlined" sx={WS_SURFACE_SX}>
                  <CardContent
                    sx={{
                      px: { xs: 2, sm: 2.5 },
                      pt: { xs: 2, sm: 2.5 },
                      pb: { xs: 3.5, sm: 4.5 },
                      "&:last-child": {
                        pb: { xs: 3.5, sm: 4.5 },
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
                      <StorageOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.35 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {t("platformPage.detailCardDominioLegacy")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.5, maxWidth: 880 }}>
                          {t("platformPage.detailCardDominioLegacyHint")}
                        </Typography>
                      </Box>
                    </Stack>
                    {Number(dominioRow?.excluido) === 0 ? (
                      <Alert
                        severity="info"
                        variant="outlined"
                        sx={{
                          mb: 2.5,
                          alignItems: "flex-start",
                          overflow: "visible",
                          flexWrap: "wrap",
                          rowGap: 1.25,
                          columnGap: 1,
                          "& .MuiAlert-message": {
                            flex: "1 1 220px",
                            minWidth: 0,
                            paddingRight: 0,
                          },
                          "& .MuiAlert-action": {
                            flex: "0 1 auto",
                            marginRight: 0,
                            paddingLeft: { xs: 0, sm: 2 },
                            alignSelf: { xs: "stretch", sm: "flex-start" },
                            width: { xs: "100%", sm: "auto" },
                          },
                        }}
                        action={
                          <BusyButton
                            variant="outlined"
                            size="small"
                            onClick={handleSyncTenantLogin}
                            loading={syncLoginLoading}
                            loadingText={t("platformPage.syncTenantLoginRunning")}
                            sx={{
                              width: { xs: "100%", sm: "auto" },
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                              flexShrink: 0,
                            }}
                          >
                            {t("platformPage.syncTenantLoginButton")}
                          </BusyButton>
                        }
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                          {t("platformPage.syncTenantLoginHint")}
                        </Typography>
                      </Alert>
                    ) : null}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "minmax(0, 1fr)",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: { xs: 1.5, sm: 2 },
                        width: "100%",
                        minWidth: 0,
                        pb: { xs: 1, sm: 1 },
                      }}
                    >
                      <Typography variant="subtitle2" component="h3" sx={{ ...TENANT_METRICS_SECTION_TITLE_SX, mt: 0 }}>
                        {t("platformPage.tenantMetricsSectionIdentity")}
                      </Typography>
                      <Box sx={{ minWidth: 0, display: "flex" }}>
                        <TenantMetricTile icon={EventOutlinedIcon} label={t("platformPage.colWorkspaceCreated")}>
                          {fmtDate(dominioRow?.data_cadastro)}
                        </TenantMetricTile>
                      </Box>
                      <Box sx={{ minWidth: 0, display: "flex" }}>
                        <TenantMetricTile icon={GroupsOutlinedIcon} label={t("platformPage.rowContractedUsers")}>
                          {dominioRow?.usuarios_contratados != null ? Number(dominioRow.usuarios_contratados).toLocaleString() : "—"}
                        </TenantMetricTile>
                      </Box>
                      <Box sx={{ minWidth: 0, display: "flex" }}>
                        <TenantMetricTile icon={LoginOutlinedIcon} label={t("platformPage.colLastTenantLogin")}>
                          {fmtDate(tenantLegacy?.tenant_last_user_access_at)}
                        </TenantMetricTile>
                      </Box>
                      <Box sx={{ minWidth: 0, display: "flex" }}>
                        <TenantMetricTile icon={InsightsOutlinedIcon} label={t("platformPage.rowLegacyInteractions")}>
                          {tenantLegacy?.legacy_interactions_30d != null
                            ? Number(tenantLegacy.legacy_interactions_30d).toLocaleString()
                            : "—"}
                        </TenantMetricTile>
                      </Box>

                      <Typography variant="subtitle2" component="h3" sx={TENANT_METRICS_SECTION_TITLE_SX}>
                        {t("platformPage.tenantMetricsSectionUsage")}
                      </Typography>
                      <Box sx={{ minWidth: 0, display: "flex", gridColumn: { xs: "auto", md: "span 2" } }}>
                        <TenantMetricTile icon={ApiOutlinedIcon} label={t("platformPage.rowBillingInteractions")}>
                          {tenantLegacy?.interactions_billing_30d != null
                            ? Number(tenantLegacy.interactions_billing_30d).toLocaleString()
                            : "—"}
                        </TenantMetricTile>
                      </Box>
                      <Box sx={{ minWidth: 0, display: "flex", gridColumn: { xs: "auto", md: "span 2" } }}>
                        <TenantMetricTile icon={LayersOutlinedIcon} label={t("platformPage.rowEffectiveInteractions")}>
                          {tenantLegacy?.usage_interactions_30d_effective != null
                            ? Number(tenantLegacy.usage_interactions_30d_effective).toLocaleString()
                            : "—"}
                        </TenantMetricTile>
                      </Box>

                      {commercialKpis?.mrr_cents != null && Number(commercialKpis.mrr_cents) > 0 ? (
                        <>
                          <Typography variant="subtitle2" component="h3" sx={TENANT_METRICS_SECTION_TITLE_SX}>
                            {t("platformPage.tenantMetricsSectionCommercial")}
                          </Typography>
                          <Box sx={{ minWidth: 0, display: "flex", gridColumn: { xs: "auto", md: "span 2" } }}>
                            <TenantMetricTile icon={AttachMoneyOutlinedIcon} label={t("platformPage.rowMrr")}>
                              {centsToBrl(commercialKpis.mrr_cents)}
                            </TenantMetricTile>
                          </Box>
                          <Box sx={{ minWidth: 0, display: "flex", gridColumn: { xs: "auto", md: "span 2" } }}>
                            <TenantMetricTile
                              icon={CalculateOutlinedIcon}
                              label={t("platformPage.rowCostPerIxShort")}
                              labelTooltip={t("platformPage.rowCostPerIx")}
                            >
                              {commercialKpis.estimated_cost_per_interaction_cents != null
                                ? centsToBrl(commercialKpis.estimated_cost_per_interaction_cents)
                                : "—"}
                            </TenantMetricTile>
                          </Box>
                        </>
                      ) : null}
                      {commercialKpis?.superlogica_mrr_cents != null && Number(commercialKpis.superlogica_mrr_cents) > 0 ? (
                        <>
                          <Typography variant="subtitle2" component="h3" sx={TENANT_METRICS_SECTION_TITLE_SX}>
                            {t("platformPage.tenantMetricsSectionSuperlogica")}
                          </Typography>
                          <Box sx={{ minWidth: 0, display: "flex", gridColumn: { xs: "auto", md: "span 2" } }}>
                            <TenantMetricTile icon={AccountBalanceOutlinedIcon} label={t("platformPage.rowSuperlogicaMrr", { defaultValue: "MRR (Superlógica)" })}>
                              {centsToBrl(commercialKpis.superlogica_mrr_cents)}
                            </TenantMetricTile>
                          </Box>
                          {commercialKpis?.superlogica_plan_name ? (
                            <Box sx={{ minWidth: 0, display: "flex", gridColumn: { xs: "auto", md: "span 2" } }}>
                              <TenantMetricTile icon={CategoryOutlinedIcon} label={t("platformPage.rowSuperlogicaPlan", { defaultValue: "Plano (Superlógica)" })}>
                                {commercialKpis.superlogica_plan_name}
                                {commercialKpis?.superlogica_commercial?.plan_grade
                                  ? ` · ${commercialKpis.superlogica_commercial.plan_grade}`
                                  : ""}
                              </TenantMetricTile>
                            </Box>
                          ) : null}
                        </>
                      ) : null}
                      {commercialKpis?.estimated_cost_per_interaction_cents != null &&
                      !(Number(commercialKpis.mrr_cents || 0) > 0) &&
                      commercialKpis?.superlogica_mrr_cents != null &&
                      Number(commercialKpis.superlogica_mrr_cents) > 0 ? (
                        <>
                          <Typography variant="subtitle2" component="h3" sx={TENANT_METRICS_SECTION_TITLE_SX}>
                            {t("platformPage.tenantMetricsSectionCommercial")}
                          </Typography>
                          <Box sx={{ minWidth: 0, display: "flex", gridColumn: { xs: "auto", md: "span 2" } }}>
                            <TenantMetricTile
                              icon={CalculateOutlinedIcon}
                              label={t("platformPage.rowCostPerIxShort")}
                              labelTooltip={t("platformPage.rowCostPerIx")}
                            >
                              {centsToBrl(commercialKpis.estimated_cost_per_interaction_cents)}
                            </TenantMetricTile>
                          </Box>
                        </>
                      ) : null}
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          ) : null}

          {innerTab === 1 ? (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={1}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {t("platformPage.detailTabHealth")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 920 }}>
                    {t("platformPage.healthIntro")}
                  </Typography>
                  {healthSnap?.generated_at ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      {t("platformPage.healthLastUpdated", { defaultValue: "Última atualização:" })}{" "}
                      {new Date(healthSnap.generated_at).toLocaleString(locale)}
                    </Typography>
                  ) : null}
                </Box>
                <BusyButton
                  size="small"
                  variant="outlined"
                  onClick={loadHealthSnapshot}
                  loading={healthLoading}
                  loadingText={t("billingPage.refresh")}
                >
                  {t("billingPage.refresh")}
                </BusyButton>
              </Stack>
              {healthError ? (
                <Alert severity="error">
                  {healthError}
                  <Button size="small" onClick={loadHealthSnapshot} sx={{ ml: 1 }}>
                    {t("billingPage.refresh")}
                  </Button>
                </Alert>
              ) : null}
              {healthLoading && !healthSnap ? (
                <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={32} />
                </Box>
              ) : null}
              {healthSnap && healthSnap.ok === false ? (
                <Alert severity="warning">{healthSnap.code || t("platformPage.healthUnavailable")}</Alert>
              ) : null}
              {healthSnap && healthSnap.ok !== false ? (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                      gap: 2,
                    }}
                  >
                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                        {t("platformPage.healthKpiTotalRequests")}
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.1 }}>
                        {healthSnap.attendances?.total_30d != null
                          ? Number(healthSnap.attendances.total_30d).toLocaleString(locale)
                          : "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("platformPage.healthKpiWindow", { days: healthSnap.period_days || 30 })}
                      </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                        {t("platformPage.healthKpiOperatorsActive")}
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.1 }}>
                        {healthSnap.user_retention?.active_operators_30d != null
                          ? Number(healthSnap.user_retention.active_operators_30d).toLocaleString(locale)
                          : "—"}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          /{" "}
                          {healthSnap.user_retention?.operators_total != null
                            ? Number(healthSnap.user_retention.operators_total).toLocaleString(locale)
                            : "—"}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("platformPage.healthKpiOperatorsHint")}
                      </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                        {t("platformPage.healthKpiFirstHuman")}
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.1 }}>
                        {fmtMinutesToShortLabel(healthSnap.time?.avg_first_human_response_minutes, locale)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {healthSnap.time?.first_human_sample != null
                          ? t("platformPage.healthKpiSample", { n: healthSnap.time.first_human_sample })
                          : "—"}
                      </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                        {t("platformPage.healthKpiResolution")}
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.1 }}>
                        {fmtMinutesToShortLabel(healthSnap.time?.avg_resolution_minutes_closed_leads, locale)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {healthSnap.time?.closed_leads_sample != null
                          ? t("platformPage.healthKpiSample", { n: healthSnap.time.closed_leads_sample })
                          : "—"}
                      </Typography>
                    </Paper>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                      gap: 2,
                    }}
                  >
                    <Paper variant="outlined" sx={{ p: 2.25, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        {t("platformPage.healthSectionRetention")}
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthOperatorsActive")}:{" "}
                        <strong>{healthSnap.user_retention?.active_operators_30d ?? "—"}</strong> /{" "}
                        {healthSnap.user_retention?.operators_total ?? "—"}
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthOperatorsInactive")}:{" "}
                        <strong>{healthSnap.user_retention?.inactive_operators_30d ?? "—"}</strong>
                      </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.25, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        {t("platformPage.healthSectionAttendances", { days: healthSnap.period_days || 30 })}
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthTotalLeads")}: <strong>{healthSnap.attendances?.total_30d ?? "—"}</strong>
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthOpen")}: {healthSnap.attendances?.aberto_30d ?? "—"} ·{" "}
                        {t("platformPage.healthInProgress")}: {healthSnap.attendances?.em_andamento_30d ?? "—"} ·{" "}
                        {t("platformPage.healthClosed")}: {healthSnap.attendances?.finalizado_30d ?? "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {t("platformPage.healthClientsDistinct")}:{" "}
                        {healthSnap.attendances?.distinct_clients_with_new_lead ?? "—"}
                      </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.25, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        {t("platformPage.healthSectionChannels")}
                      </Typography>
                      {(healthSnap.channels || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t("platformPage.healthEmptyChannels")}
                        </Typography>
                      ) : (
                        <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                          <Table size="small" sx={WS_TABLE_DENSE_SX}>
                            <TableHead>
                              <TableRow>
                                <TableCell>{t("platformPage.healthColChannel")}</TableCell>
                                <TableCell align="right">{t("platformPage.quantity")}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(healthSnap.channels || []).map((c) => (
                                <TableRow key={`${c.channel_tipo}-${c.channel_name}`}>
                                  <TableCell>{c.channel_name}</TableCell>
                                  <TableCell align="right">{Number(c.count || 0).toLocaleString()}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.25, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        {t("platformPage.healthSectionOperators")}
                      </Typography>
                      {(healthSnap.operator_messages || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t("platformPage.healthEmptyOperators")}
                        </Typography>
                      ) : (
                        <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                          <Table size="small" sx={WS_TABLE_DENSE_SX}>
                            <TableHead>
                              <TableRow>
                                <TableCell>{t("platformPage.healthColOperator")}</TableCell>
                                <TableCell align="right">{t("platformPage.healthColMessages")}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(healthSnap.operator_messages || []).map((o) => (
                                <TableRow key={o.user_id}>
                                  <TableCell>{o.name}</TableCell>
                                  <TableCell align="right">{Number(o.message_count || 0).toLocaleString()}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.25, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        {t("platformPage.healthSectionBotProxy")}
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthWithAgent")}: {healthSnap.bot_proxy?.leads_with_agent_30d ?? "—"}
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthWithoutAgent")}: {healthSnap.bot_proxy?.leads_without_agent_30d ?? "—"}
                      </Typography>
                      {healthSnap.bot_proxy?.note ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {healthSnap.bot_proxy.note}
                        </Typography>
                      ) : null}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.25, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        {t("platformPage.healthSectionTime")}
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthTtrClosed")}:{" "}
                        {healthSnap.time?.avg_resolution_minutes_closed_leads != null
                          ? `${fmtMinutesToShortLabel(healthSnap.time.avg_resolution_minutes_closed_leads, locale)} (n=${healthSnap.time.closed_leads_sample})`
                          : "—"}
                      </Typography>
                      <Typography variant="body2">
                        {t("platformPage.healthFirstHuman")}:{" "}
                        {healthSnap.time?.avg_first_human_response_minutes != null
                          ? `${fmtMinutesToShortLabel(healthSnap.time.avg_first_human_response_minutes, locale)} (n=${healthSnap.time.first_human_sample})`
                          : "—"}
                      </Typography>
                    </Paper>

                    {healthSnap.time_to_value?.days_workspace_to_first_legacy_interaction != null ? (
                      <Paper variant="outlined" sx={{ p: 2.25, ...WS_SURFACE_SX }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                          {t("platformPage.healthSectionTtv")}
                        </Typography>
                        <Typography variant="body2">
                          {t("platformPage.healthTtvDays", {
                            days: healthSnap.time_to_value.days_workspace_to_first_legacy_interaction,
                          })}
                        </Typography>
                        {healthSnap.time_to_value?.note ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {healthSnap.time_to_value.note}
                          </Typography>
                        ) : null}
                      </Paper>
                    ) : null}
                  </Box>
                </>
              ) : null}
            </Stack>
          ) : null}

          {innerTab === 2 ? (
            <Card variant="outlined" sx={WS_SURFACE_SX}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {usageData?.period
                    ? t("billingPage.usagePeriod", {
                        from: usageData.period.from,
                        to: usageData.period.to,
                      })
                    : ""}
                </Typography>
                {usageData?.legacy_fallback ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t("platformPage.usageLegacyFallbackNote")}
                  </Alert>
                ) : null}
                {usageData?.metrics?.length ? (
                  <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                    <Table size="small" sx={WS_TABLE_DENSE_SX}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("platformPage.metric")}</TableCell>
                          <TableCell align="right">{t("platformPage.quantity")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageData.metrics.map((m) => (
                          <TableRow key={m.metric_key}>
                            <TableCell>{metricLabel(m.metric_key)}</TableCell>
                            <TableCell align="right">{Number(m.total_quantity).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("billingPage.usageEmpty")}
                  </Typography>
                )}
                <Button size="small" sx={{ mt: 2 }} onClick={() => loadDetailBundle()}>
                  {t("billingPage.refresh")}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {innerTab === 3 ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {t("platformPage.invoicesSectionXpro", { defaultValue: "Faturas (billing Xpro)" })}
                </Typography>
                <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={WS_TABLE_DENSE_SX}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("platformPage.invoiceColNumber")}</TableCell>
                        <TableCell>{t("platformPage.invoiceColStatus")}</TableCell>
                        <TableCell align="right">{t("platformPage.invoiceTotal")}</TableCell>
                        <TableCell>{t("platformPage.due")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t("billingPage.invoicesEmptyTitle")}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoices.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell>{m.number_code || m.id}</TableCell>
                            <TableCell>{m.status}</TableCell>
                            <TableCell align="right">{centsToBrl(m.total_cents)}</TableCell>
                            <TableCell>{fmtDate(m.due_at)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {t("platformPage.invoicesSectionSuperlogica", {
                    defaultValue: "Cobranças e notas fiscais (Superlógica)",
                  })}
                </Typography>
                {slFinanceLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">
                      {t("platformPage.invoicesSlLoading", { defaultValue: "Carregando financeiro Superlógica…" })}
                    </Typography>
                  </Stack>
                ) : null}
                {slFinanceError ? (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {slFinanceError}
                  </Alert>
                ) : null}
                {!slFinanceLoading && slFinance?.not_linked ? (
                  <Alert severity="info" variant="outlined">
                    {t("platformPage.invoicesSlNotLinked", {
                      defaultValue:
                        "Workspace sem vínculo Superlógica. Vincule na aba Superlógica para ver cobranças e NFs aqui.",
                    })}
                  </Alert>
                ) : null}
                {!slFinanceLoading && slFinance && !slFinance.not_linked ? (
                  <Stack spacing={1.5}>
                    {slFinance?.errors?.invoices ? (
                      <Alert severity="warning" variant="outlined">
                        {t("platformPage.invoicesSlCobrancasError", {
                          defaultValue: "Cobranças Superlógica: {{msg}}",
                          msg: slFinance.errors.invoices.message || "—",
                        })}
                      </Alert>
                    ) : null}
                    {slFinance?.errors?.nfs ? (
                      <Alert severity="warning" variant="outlined">
                        {t("platformPage.invoicesSlNfsError", {
                          defaultValue: "NFs Superlógica: {{msg}}",
                          msg: slFinance.errors.nfs.message || "—",
                        })}
                      </Alert>
                    ) : null}
                    <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                      <Table size="small" sx={WS_TABLE_DENSE_SX}>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("platformPage.invoiceColNumber")}</TableCell>
                            <TableCell>{t("platformPage.invoiceColStatus")}</TableCell>
                            <TableCell align="right">{t("platformPage.invoiceTotal")}</TableCell>
                            <TableCell>{t("platformPage.due")}</TableCell>
                            <TableCell>{t("platformPage.invoicesColNfs", { defaultValue: "NFs" })}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!Array.isArray(slFinance.items) || slFinance.items.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">
                                  {t("platformPage.invoicesSlEmpty", {
                                    defaultValue: "Nenhuma cobrança retornada para este cliente.",
                                  })}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            slFinance.items.map((row, idx) => (
                              <TableRow key={`${row.cobranca_id || row.display_ref}-${idx}`}>
                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                                  {row.display_ref}
                                </TableCell>
                                <TableCell>{row.status}</TableCell>
                                <TableCell align="right">
                                  {row.total_cents != null ? centsToBrl(row.total_cents) : "—"}
                                </TableCell>
                                <TableCell>{row.due_raw ? fmtDate(row.due_raw) : "—"}</TableCell>
                                <TableCell>
                                  {row.nfs?.length ? (
                                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                                      {row.nfs.map((nf, i) => (
                                        <Chip
                                          key={`${nf.nf_id || nf.numero || i}-${i}`}
                                          size="small"
                                          variant="outlined"
                                          label={
                                            nf.numero ||
                                            nf.nf_id ||
                                            t("platformPage.invoiceNfNoNumber", { defaultValue: "NF" })
                                          }
                                          title={
                                            [nf.emissao, nf.status_nf].filter(Boolean).join(" · ") || undefined
                                          }
                                        />
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {Array.isArray(slFinance.nfs_without_cobranca) && slFinance.nfs_without_cobranca.length > 0 ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {t("platformPage.invoicesSlOrphanNfs", {
                            defaultValue: "NFs sem vínculo explícito com cobrança nesta resposta ({{n}})",
                            n: slFinance.nfs_without_cobranca.length,
                          })}
                        </Typography>
                        <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                          <Table size="small" sx={WS_TABLE_DENSE_SX}>
                            <TableHead>
                              <TableRow>
                                <TableCell>{t("platformPage.invoiceColNumber")}</TableCell>
                                <TableCell>{t("platformPage.due")}</TableCell>
                                <TableCell>{t("platformPage.invoicesColCobrancaRef", { defaultValue: "Ref. cobrança" })}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {slFinance.nfs_without_cobranca.map((nf, i) => (
                                <TableRow key={`orph-${nf.nf_id || i}`}>
                                  <TableCell>{nf.numero || nf.nf_id || "—"}</TableCell>
                                  <TableCell>{nf.emissao ? fmtDate(nf.emissao) : "—"}</TableCell>
                                  <TableCell>{nf.raw_ref_cobranca || "—"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ) : null}
                    <Button size="small" variant="outlined" onClick={() => loadFinanceTab()} disabled={slFinanceLoading}>
                      {t("billingPage.refresh")}
                    </Button>
                  </Stack>
                ) : null}
              </Box>
            </Stack>
          ) : null}

          {innerTab === 4 ? (
            offerAnalytics.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                {t("platformPage.offerAnalyticsEmpty")}
              </Alert>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {offerAnalytics.map((row) => (
                  <Chip key={row.event_type} size="small" color="primary" variant="outlined" label={`${row.event_type}: ${row.total}`} />
                ))}
              </Stack>
            )
          ) : null}

          {innerTab === 5 ? (
            <Stack spacing={3} maxWidth={720}>
              {!canSegmentsView ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  {t("platformPage.workspaceOptionalSegmentsHint")}
                </Alert>
              ) : null}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t("platformPage.segmentBlockTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("platformPage.segmentIntro")}
                </Typography>
                <TextField
                  select
                  fullWidth
                  label={t("platformPage.segmentSelectValue")}
                  value={segmentValue}
                  onChange={(e) => setSegmentValue(e.target.value)}
                  disabled={segmentSaving}
                  sx={{ maxWidth: 560 }}
                >
                  {segmentSelectOptions.map((row) => {
                    const v = row.segmentKey;
                    return (
                      <MenuItem key={v} value={v}>
                        {row.displayName ||
                          t(`platformPage.segmentOption.${v}`, { defaultValue: v })}
                      </MenuItem>
                    );
                  })}
                </TextField>
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={handleSaveSegment} disabled={segmentSaving}>
                    {segmentSaving ? t("platformPage.segmentSaving") : t("platformPage.segmentSave")}
                  </Button>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t("platformPage.aiContextHeading")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("platformPage.aiContextIntro")}
                </Typography>
                {aiCtxLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">
                      {t("platformPage.aiContextLoading")}
                    </Typography>
                  </Stack>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxCompanyName")}
                        value={aiCtxForm.companyName}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, companyName: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxWebsite")}
                        value={aiCtxForm.companyWebsite}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, companyWebsite: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label={t("platformPage.aiCtxAbout", { defaultValue: "Sobre" })}
                        value={aiCtxForm.companyAbout}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, companyAbout: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxSegmento")}
                        value={aiCtxForm.segmento}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, segmento: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxSegmentKey")}
                        value={aiCtxForm.segmentKey}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, segmentKey: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxAudience")}
                        value={aiCtxForm.publicoAlvo}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, publicoAlvo: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxFocus")}
                        value={aiCtxForm.focoPrincipal}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, focoPrincipal: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxMaturity")}
                        value={aiCtxForm.maturidadeDigital}
                        onChange={(e) => setAiCtxForm((p) => ({ ...p, maturidadeDigital: e.target.value }))}
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.aiCtxStrategy")}
                        value={aiCtxForm.capacidadeEstrategica}
                        onChange={(e) =>
                          setAiCtxForm((p) => ({ ...p, capacidadeEstrategica: e.target.value }))
                        }
                        disabled={aiCtxSaving}
                      />
                    </Grid>
                    {aiCtxForm._updatedAt ? (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {t("platformPage.aiCtxAudit", {
                            at: fmtDate(aiCtxForm._updatedAt),
                            uid: aiCtxForm._updatedByUserId || "—",
                          })}
                        </Typography>
                      </Grid>
                    ) : null}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSaveAiWorkspaceContext}
                        disabled={aiCtxSaving}
                      >
                        {aiCtxSaving
                          ? t("platformPage.aiContextSaving")
                          : t("platformPage.aiContextSave")}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Stack>
          ) : null}

          {innerTab === 6 ? (
            <Grid container spacing={2} maxWidth={720}>
              {!canCatalogView ? (
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    {t("platformPage.workspaceOptionalCatalogHint")}
                  </Alert>
                </Grid>
              ) : null}
              {!canGovernanceView ? (
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    {t("platformPage.workspaceOptionalGovernanceHint")}
                  </Alert>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t("platformPage.assignIntro")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                    {t("platformPage.assignPlanTitle")}
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t("platformPage.assignPlanSku")}
                        value={assignForm.planSku}
                        onChange={(e) => setAssignForm((p) => ({ ...p, planSku: e.target.value }))}
                        disabled={assigning || !selectablePlans.length}
                      >
                        {selectablePlans.map((p) => (
                          <MenuItem key={`${p.sku}-${p.version}`} value={p.sku}>
                            {p.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t("billingPage.adminFieldVersionNumber")}
                        value={String(assignForm.version)}
                        onChange={(e) =>
                          setAssignForm((p) => ({ ...p, version: Number(e.target.value) || 1 }))
                        }
                        disabled={assigning}
                      >
                        {Array.from(
                          new Set(
                            selectablePlans.filter((p) => p.sku === assignForm.planSku).map((p) => p.version)
                          )
                        ).map((v) => (
                          <MenuItem key={v} value={String(v)}>
                            v{v}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t("billingPage.adminFieldBillingMode")}
                        value={assignForm.billingMode}
                        onChange={(e) => setAssignForm((p) => ({ ...p, billingMode: e.target.value }))}
                        disabled={assigning}
                      >
                        <MenuItem value="postpaid">{billingModeLabel("postpaid")}</MenuItem>
                        <MenuItem value="prepaid">{billingModeLabel("prepaid")}</MenuItem>
                        <MenuItem value="hybrid">{billingModeLabel("hybrid")}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t("billingPage.adminFieldSubscriptionStatus")}
                        value={assignForm.status}
                        onChange={(e) => setAssignForm((p) => ({ ...p, status: e.target.value }))}
                        disabled={assigning}
                      >
                        <MenuItem value="trialing">{subscriptionStatusLabel("trialing")}</MenuItem>
                        <MenuItem value="active">{subscriptionStatusLabel("active")}</MenuItem>
                        <MenuItem value="past_due">{subscriptionStatusLabel("past_due")}</MenuItem>
                        <MenuItem value="suspended">{subscriptionStatusLabel("suspended")}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t("platformPage.assignReseller")}
                        value={assignForm.resellerId}
                        onChange={(e) => setAssignForm((p) => ({ ...p, resellerId: e.target.value }))}
                        disabled={assigning}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="">{t("platformPage.assignResellerNone")}</MenuItem>
                        {resellerList.map((r) => (
                          <MenuItem key={r.id} value={String(r.id)}>
                            {r.name || r.code} ({r.code})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!assignForm.enforceLimits}
                            onChange={(e) =>
                              setAssignForm((p) => ({ ...p, enforceLimits: e.target.checked }))
                            }
                            disabled={assigning}
                          />
                        }
                        label={t("billingPage.adminFieldEnforceLimits")}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <BusyButton variant="contained" onClick={handleAssign} loading={assigning} loadingText={t("billingPage.saving")}>
                        {t("platformPage.assignSubmit")}
                      </BusyButton>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              {slLoading ? (
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" color="text.secondary">
                      {t("platformPage.superlogicaPlanTabLoading", { defaultValue: "Sincronizando vínculo Superlógica…" })}
                    </Typography>
                  </Stack>
                </Grid>
              ) : null}
              {slLink?.superlogica_cliente_id ? (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, bgcolor: (th) => alpha(th.palette.info.main, 0.06) }}
                  >
                    <Typography variant="overline" color="text.secondary" display="block">
                      {t("platformPage.planTabSuperlogicaTitle", {
                        defaultValue: "Plano no Superlógica (cliente vinculado)",
                      })}
                    </Typography>
                    {slSummary?.commercial_snapshot?.plan_name ||
                    (Array.isArray(slSummary?.contracts) && slSummary.contracts[0]?.st_nome_pla) ? (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        <Typography variant="body1" fontWeight={700}>
                          {slSummary?.commercial_snapshot?.plan_name ||
                            slSummary?.contracts?.[0]?.st_nome_pla ||
                            "—"}
                          {slSummary?.commercial_snapshot?.plan_grade
                            ? ` · ${slSummary.commercial_snapshot.plan_grade}`
                            : slSummary?.contracts?.[0]?.st_nome_gpl
                              ? ` · ${slSummary.contracts[0].st_nome_gpl}`
                              : ""}
                        </Typography>
                        {slSummary?.commercial_snapshot?.mrr_cents != null &&
                        Number(slSummary.commercial_snapshot.mrr_cents) > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            MRR: {centsToBrl(slSummary.commercial_snapshot.mrr_cents)}
                          </Typography>
                        ) : null}
                        {(slSummary?.commercial_snapshot?.id_plano_pla ||
                          slSummary?.contracts?.[0]?.id_plano_pla) != null ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("platformPage.superlogicaPlanIdLabel", { defaultValue: "ID plano Superlógica:" })}{" "}
                            <strong>
                              {String(
                                slSummary?.commercial_snapshot?.id_plano_pla ??
                                  slSummary?.contracts?.[0]?.id_plano_pla ??
                                  ""
                              )}
                            </strong>
                            {slSummary?.commercial_snapshot?.dt_contrato_plc ||
                            slSummary?.contracts?.[0]?.dt_contrato_plc ? (
                              <>
                                {" "}
                                · {t("platformPage.superlogicaContractDate", { defaultValue: "Contrato:" })}{" "}
                                {slSummary?.commercial_snapshot?.dt_contrato_plc ||
                                  slSummary?.contracts?.[0]?.dt_contrato_plc}
                              </>
                            ) : null}
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t("platformPage.planTabSuperlogicaNoPlan", {
                          defaultValue:
                            "Nenhum contrato/plano retornado na última sincronização. Abra a aba Superlógica e use Atualizar.",
                        })}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined">
                    {t("platformPage.invoicesSlNotLinked", {
                      defaultValue:
                        "Workspace sem vínculo Superlógica. Vincule na aba Superlógica para ver cobranças e NFs aqui.",
                    })}
                  </Alert>
                </Grid>
              )}
            </Grid>
          ) : null}

          {innerTab === 7 ? (
            <Stack spacing={2}>
              {!canGovernanceView ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  {t("platformPage.workspaceOptionalGovernanceHint")}
                </Alert>
              ) : null}
              <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                <Table size="small" sx={WS_TABLE_DENSE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("platformPage.auditWhen")}</TableCell>
                      <TableCell>{t("platformPage.auditAction")}</TableCell>
                      <TableCell>{t("platformPage.auditActor")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audit.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            {!canGovernanceView
                              ? t("platformPage.auditEmptyNoGovernance")
                              : t("platformPage.auditEmpty")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      audit.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{fmtDate(a.created_at)}</TableCell>
                          <TableCell>{a.action}</TableCell>
                          <TableCell>{a.actor_user_id ?? "—"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          ) : null}

          {innerTab === 8 ? (
            <Stack spacing={2} maxWidth={920}>
              {slError ? (
                <Alert severity="error" onClose={() => setSlError("")}>
                  {slError}
                </Alert>
              ) : null}

              <Dialog open={slPickOpen} onClose={() => !slLoading && setSlPickOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t("platformPage.superlogicaPickTitle", { defaultValue: "Selecione o cliente Superlógica" })}</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("platformPage.superlogicaPickBody", {
                      defaultValue: "Encontramos mais de um cliente para o documento informado. Escolha o correto para vincular ao workspace.",
                    })}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label={t("platformPage.superlogicaClient", { defaultValue: "Cliente" })}
                    value={slPickId}
                    onChange={(e) => setSlPickId(e.target.value)}
                    disabled={slLoading}
                  >
                    {slCandidates.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {String(c.label || c.id)}
                      </MenuItem>
                    ))}
                  </TextField>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={() => setSlPickOpen(false)} disabled={slLoading} color="inherit">
                    {t("billingPage.cancel", { defaultValue: "Cancelar" })}
                  </Button>
                  <Button onClick={doForcePickCandidate} disabled={slLoading || !slPickId} variant="contained">
                    {slLoading ? t("billingPage.saving", { defaultValue: "Salvando..." }) : t("billingPage.confirm", { defaultValue: "Confirmar" })}
                  </Button>
                </DialogActions>
              </Dialog>

              <Card variant="outlined" sx={WS_SURFACE_SX}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {t("platformPage.superlogicaSectionLink", { defaultValue: "Vínculo Superlógica" })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("platformPage.superlogicaSectionHint", {
                          defaultValue: "Vincule este workspace a um cliente no Superlógica para carregar contratos, cobranças e NFs.",
                        })}
                      </Typography>
                    </Box>
                    <BusyButton size="small" variant="outlined" onClick={loadSuperlogica} loading={slLoading} loadingText={t("billingPage.refresh")}>
                      {t("billingPage.refresh")}
                    </BusyButton>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("platformPage.superlogicaDoc", { defaultValue: "CPF/CNPJ" })}
                        value={slDoc}
                        onChange={(e) => setSlDoc(normalizeDocPreview(e.target.value))}
                        disabled={slLoading}
                        placeholder="00.000.000/0000-00"
                      />
                    </Grid>
                    <Grid item xs={12} md="auto">
                      <BusyButton variant="contained" onClick={doLinkByDoc} loading={slLoading} loadingText={t("billingPage.saving", { defaultValue: "Salvando..." })} disabled={!slDoc}>
                        {t("platformPage.superlogicaLinkBtn", { defaultValue: "Vincular" })}
                      </BusyButton>
                    </Grid>
                    <Grid item xs={12} md="auto">
                      <Button variant="outlined" color="error" onClick={doUnlink} disabled={slLoading || !slLink}>
                        {t("platformPage.superlogicaUnlinkBtn", { defaultValue: "Desvincular" })}
                      </Button>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("platformPage.superlogicaCurrentLink", { defaultValue: "Vínculo atual:" })}{" "}
                      <strong>{slLink?.superlogica_cliente_id || "—"}</strong>
                      {slLink?.doc_value_normalized ? (
                        <>
                          {" "}
                          · {t("platformPage.superlogicaDoc", { defaultValue: "CPF/CNPJ" })}:{" "}
                          <strong>{slLink.doc_value_normalized}</strong>
                        </>
                      ) : null}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={WS_SURFACE_SX}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t("platformPage.superlogicaSectionSummary", { defaultValue: "Resumo (Superlógica)" })}
                  </Typography>
                  {!slSummary ? (
                    <Alert severity="info" variant="outlined">
                      {slLink
                        ? t("platformPage.superlogicaSummaryEmpty", { defaultValue: "Clique em Atualizar para carregar o resumo." })
                        : t("platformPage.superlogicaNotLinkedHint", { defaultValue: "Vincule um cliente para carregar informações do Superlógica." })}
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {slSummary?.commercial_snapshot ? (
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX, bgcolor: (th) => alpha(th.palette.primary.main, 0.04) }}>
                            <Typography variant="overline" color="text.secondary">
                              {t("platformPage.superlogicaCommercialBlock", { defaultValue: "Dados comerciais (contrato ativo)" })}
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              {slSummary.commercial_snapshot.mrr_cents != null &&
                              Number(slSummary.commercial_snapshot.mrr_cents) > 0 ? (
                                <Grid item xs={6} sm={4}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    MRR
                                  </Typography>
                                  <Typography variant="h6" fontWeight={800}>
                                    {centsToBrl(slSummary.commercial_snapshot.mrr_cents)}
                                  </Typography>
                                </Grid>
                              ) : null}
                              {slSummary.commercial_snapshot.plan_name ? (
                                <Grid item xs={12} sm={8}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {t("platformPage.rowSuperlogicaPlan", { defaultValue: "Plano (Superlógica)" })}
                                  </Typography>
                                  <Typography variant="body1" fontWeight={700}>
                                    {slSummary.commercial_snapshot.plan_name}
                                    {slSummary.commercial_snapshot.plan_grade
                                      ? ` · ${slSummary.commercial_snapshot.plan_grade}`
                                      : ""}
                                  </Typography>
                                  {slSummary.commercial_snapshot.dt_contrato_plc ? (
                                    <Typography variant="caption" color="text.secondary">
                                      {t("platformPage.superlogicaContractDate", { defaultValue: "Contrato:" })}{" "}
                                      {slSummary.commercial_snapshot.dt_contrato_plc}
                                    </Typography>
                                  ) : null}
                                </Grid>
                              ) : null}
                            </Grid>
                          </Paper>
                        </Grid>
                      ) : null}

                      {slSummary?.errors?.invoices?.status === 404 ? (
                        <Grid item xs={12}>
                          <Alert severity="warning" variant="outlined">
                            {t("platformPage.superlogicaInvoices404", {
                              defaultValue:
                                "Cobranças: a API retornou 404 (rota pode variar por conta). Contratos e MRR já vêm do endpoint de assinaturas.",
                            })}
                          </Alert>
                        </Grid>
                      ) : null}
                      {slSummary?.errors?.nfs?.status === 404 ? (
                        <Grid item xs={12}>
                          <Alert severity="warning" variant="outlined">
                            {t("platformPage.superlogicaNfs404", {
                              defaultValue:
                                "Notas fiscais: a API retornou 404. O módulo ou path pode não estar habilitado para este token.",
                            })}
                          </Alert>
                        </Grid>
                      ) : null}

                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                          <Typography variant="overline" color="text.secondary">
                            {t("platformPage.superlogicaContracts", { defaultValue: "Contratos" })}
                          </Typography>
                          <Typography variant="h5" fontWeight={800}>
                            {Array.isArray(slSummary.contracts) ? slSummary.contracts.length.toLocaleString(locale) : "—"}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                          <Typography variant="overline" color="text.secondary">
                            {t("platformPage.superlogicaInvoices", { defaultValue: "Cobranças/Faturas" })}
                          </Typography>
                          <Typography variant="h5" fontWeight={800}>
                            {Array.isArray(slSummary.invoices) ? slSummary.invoices.length.toLocaleString(locale) : "—"}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                          <Typography variant="overline" color="text.secondary">
                            {t("platformPage.superlogicaNfs", { defaultValue: "NFs" })}
                          </Typography>
                          <Typography variant="h5" fontWeight={800}>
                            {Array.isArray(slSummary.nfs) ? slSummary.nfs.length.toLocaleString(locale) : "—"}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Stack>
          ) : null}

          {innerTab === 9 ? (
            <Stack spacing={2.5} maxWidth={980}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <DirectionsBusOutlinedIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t("platformPage.passengerSalesTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("platformPage.passengerSalesIntro")}
                    </Typography>
                  </Box>
                </Stack>
                <BusyButton
                  size="small"
                  variant="outlined"
                  onClick={() => loadPassengerSalesTab()}
                  loading={passengerSalesLoading}
                  loadingText={t("billingPage.refresh")}
                >
                  {t("billingPage.refresh")}
                </BusyButton>
              </Stack>

              {passengerSalesError ? (
                <Alert severity="error" onClose={() => setPassengerSalesError("")}>
                  {passengerSalesError}
                </Alert>
              ) : null}

              {passengerSalesLoading && !passengerSalesPayload ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    {t("platformPage.passengerSalesLoading")}
                  </Typography>
                </Stack>
              ) : null}

              {(() => {
                const snap = passengerSalesPayload?.snapshot;
                if (!snap && !passengerSalesLoading && !passengerSalesError) {
                  return (
                    <Alert severity="warning" variant="outlined">
                      {t("platformPage.passengerSalesEmpty")}
                    </Alert>
                  );
                }
                if (!snap?.ok) {
                  return (
                    <Alert severity="warning" variant="outlined">
                      {snap?.message ||
                        snap?.code ||
                        t("platformPage.passengerSalesEmpty")}
                    </Alert>
                  );
                }
                const period = snap.period || {};
                const partner = snap.partner || {};
                const funnelSteps = Array.isArray(snap.funnel?.by_step) ? snap.funnel.by_step : [];
                const payProviders = Array.isArray(snap.payments?.provider_counts) ? snap.payments.provider_counts : [];
                const formas = Array.isArray(snap.payments?.formas_de_pagamento) ? snap.payments.formas_de_pagamento : [];
                const recent = Array.isArray(snap.recent_funnel_events) ? snap.recent_funnel_events : [];
                const hints = snap.payments?.reconciliation_hints || {};

                const railLabel = (rail) =>
                  ({
                    xpay: "Xpay → Mercado Pago",
                    mercadopago: "Mercado Pago (checkout)",
                    cielo: "Cielo",
                    rede: "Rede",
                    pix_itau: "Pix Itaú",
                    appmax: "Appmax",
                    other: "—",
                    unknown: "—",
                  })[rail] || rail;

                return (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {t("platformPage.passengerSalesPeriod", {
                        from: period.from || "—",
                        to: period.to || "—",
                      })}
                    </Typography>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {t("platformPage.passengerSalesPartnerTitle")}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        {partner.primaryLabel ? (
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`${t("platformPage.passengerSalesPartnerPrimary")}: ${partner.primaryLabel}`}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("platformPage.passengerSalesPartnerUnknown")}
                          </Typography>
                        )}
                      </Stack>
                      {partner.integrationsActive?.length ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                            {t("platformPage.passengerSalesIntegrationsDetected")}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {partner.integrationsActive.map((p) => (
                              <Chip key={p.key} size="small" variant="outlined" label={p.label || p.key} />
                            ))}
                          </Stack>
                        </Box>
                      ) : null}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {t("platformPage.passengerSalesFunnelTitle")}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Chip
                          size="small"
                          label={`${t("platformPage.passengerSalesFunnelEmissao")}: ${Number(snap.funnel?.counts?.emissao || 0).toLocaleString(locale)}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${t("platformPage.passengerSalesFunnelPagamento")}: ${Number(snap.funnel?.counts?.pagamento || 0).toLocaleString(locale)}`}
                        />
                      </Stack>
                      <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                        <Table size="small" sx={WS_TABLE_DENSE_SX}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("platformPage.passengerSalesColStep")}</TableCell>
                              <TableCell align="right">{t("platformPage.passengerSalesColCount")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {funnelSteps.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={2}>
                                  <Typography variant="body2" color="text.secondary">
                                    {t("platformPage.passengerSalesNoFunnel")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              funnelSteps.map((row) => (
                                <TableRow key={row.step}>
                                  <TableCell>{row.step}</TableCell>
                                  <TableCell align="right">{Number(row.count || 0).toLocaleString(locale)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {t("platformPage.passengerSalesPaymentsTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t("platformPage.passengerSalesPaymentsSub")}
                      </Typography>
                      <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                        <Table size="small" sx={WS_TABLE_DENSE_SX}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("platformPage.passengerSalesColProvider")}</TableCell>
                              <TableCell align="right">{t("platformPage.passengerSalesColLeads")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {payProviders.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={2}>
                                  <Typography variant="body2" color="text.secondary">
                                    {t("platformPage.passengerSalesNoProviders")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              payProviders.map((row) => (
                                <TableRow key={row.provider}>
                                  <TableCell>{row.provider}</TableCell>
                                  <TableCell align="right">{Number(row.count || 0).toLocaleString(locale)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        {t("platformPage.passengerSalesReconcileTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("platformPage.passengerSalesReconcileBody", {
                          xpay: Number(hints.xpay_rows || 0).toLocaleString(locale),
                          mp: Number(hints.mercadopago_direct_rows || 0).toLocaleString(locale),
                        })}
                      </Typography>
                      {hints.note ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          {hints.note}
                        </Typography>
                      ) : null}
                    </Alert>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {t("platformPage.passengerSalesFormasTitle")}
                      </Typography>
                      <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                        <Table size="small" sx={WS_TABLE_DENSE_SX}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("platformPage.passengerSalesColForma")}</TableCell>
                              <TableCell>{t("platformPage.passengerSalesColRail")}</TableCell>
                              <TableCell align="center">{t("platformPage.passengerSalesColMp")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formas.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3}>
                                  <Typography variant="body2" color="text.secondary">
                                    {t("platformPage.passengerSalesNoFormas")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              formas.slice(0, 40).map((pm) => (
                                <TableRow key={pm.id}>
                                  <TableCell>{pm.nome}</TableCell>
                                  <TableCell>{railLabel(pm.rail)}</TableCell>
                                  <TableCell align="center">
                                    {pm.reconcileWithMercadoPago ? t("platformPage.passengerSalesYes") : "—"}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {t("platformPage.passengerSalesRecentTitle")}
                      </Typography>
                      <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                        <Table size="small" sx={WS_TABLE_DENSE_SX}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("platformPage.passengerSalesColWhen")}</TableCell>
                              <TableCell>{t("platformPage.passengerSalesColLead")}</TableCell>
                              <TableCell>{t("platformPage.passengerSalesColStep")}</TableCell>
                              <TableCell>{t("platformPage.passengerSalesColSource")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {recent.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography variant="body2" color="text.secondary">
                                    {t("platformPage.passengerSalesNoRecent")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              recent.map((ev) => (
                                <TableRow key={ev.id}>
                                  <TableCell>{fmtDate(ev.created_at)}</TableCell>
                                  <TableCell>{ev.id_lead ?? "—"}</TableCell>
                                  <TableCell>{ev.step}</TableCell>
                                  <TableCell>{ev.source || "—"}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </>
                );
              })()}
            </Stack>
          ) : null}

          {innerTab === 10 ? (
            <Stack spacing={2.5} maxWidth={1024}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "flex-start" }} justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <SyncAltOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t("platformPage.gupshupReconcileTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                      {t("platformPage.gupshupReconcileIntro")}
                    </Typography>
                  </Box>
                </Stack>
                <BusyButton
                  size="small"
                  variant="outlined"
                  onClick={() => loadGupshupReconcileTab()}
                  loading={gupshupLoading}
                  loadingText={t("billingPage.refresh")}
                >
                  {t("billingPage.refresh")}
                </BusyButton>
              </Stack>

              {gupshupPayload?.period?.from ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("platformPage.gupshupReconcilePeriod", {
                    from: gupshupPayload.period.from,
                    to: gupshupPayload.period.to,
                  })}
                  sx={{ alignSelf: "flex-start", fontWeight: 500 }}
                />
              ) : null}

              {gupshupError ? (
                <Alert severity="error" onClose={() => setGupshupError("")}>
                  {gupshupError}
                </Alert>
              ) : null}

              {gupshupLoading || (gupshupPayload === null && !gupshupError) ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    {t("platformPage.gupshupReconcileLoading")}
                  </Typography>
                </Stack>
              ) : null}

              {Array.isArray(gupshupPayload?.billing_metrics) && gupshupPayload.billing_metrics.length > 0 ? (
                <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {t("platformPage.gupshupReconcileBillingTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("platformPage.gupshupReconcileBillingSub")}
                  </Typography>
                  <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                    <Table size="small" sx={WS_TABLE_DENSE_SX}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("platformPage.gupshupReconcileColMetric")}</TableCell>
                          <TableCell align="right">{t("platformPage.gupshupReconcileColQty")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {gupshupPayload.billing_metrics.map((row) => (
                          <TableRow key={row.metric_key}>
                            <TableCell>
                              {t(`billingPage.metricKeys.${row.metric_key}`, {
                                defaultValue: String(row.metric_key || "").replace(/_/g, " ") || "—",
                              })}
                            </TableCell>
                            <TableCell align="right">{Number(row.total_quantity || 0).toLocaleString(locale)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : null}

              {(() => {
                const rec = gupshupPayload?.reconciliation;
                const waiting =
                  gupshupLoading || (gupshupPayload === null && !gupshupError);
                if (waiting) {
                  return null;
                }
                if (!rec && !gupshupError && gupshupPayload != null) {
                  return (
                    <Alert severity="warning" variant="outlined">
                      {t("platformPage.gupshupReconcileEmpty")}
                    </Alert>
                  );
                }
                if (rec && !rec.ok) {
                  return (
                    <Alert severity="warning" variant="outlined">
                      {rec?.message || rec?.code || t("platformPage.gupshupReconcileEmpty")}
                    </Alert>
                  );
                }
                if (!rec) {
                  return null;
                }
                const chans = Array.isArray(rec.channels) ? rec.channels : [];
                const partnerBase = rec.meta?.partner_api_base || "";
                const hasGlobalCred =
                  Boolean(rec.meta?.gupshup_partner_global_app_token_set) ||
                  Boolean(rec.meta?.gupshup_partner_global_portal_set) ||
                  Boolean(rec.meta?.gupshup_partner_global_numeric_id_set);
                return (
                  <>
                    <Accordion
                      variant="outlined"
                      disableGutters
                      sx={{
                        borderRadius: 2,
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t("platformPage.gupshupReconcileTechAccordionTitle")}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                        <Stack spacing={1.25}>
                          <Typography variant="body2" color="text.secondary">
                            {t("platformPage.gupshupReconcileTechIntro")}
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                            {rec.meta?.gupshup_partner_global_app_token_set ? (
                              <Chip size="small" color="success" variant="outlined" label={t("platformPage.gupshupReconcileChipToken")} />
                            ) : null}
                            {rec.meta?.gupshup_partner_global_portal_set ? (
                              <Chip size="small" color="success" variant="outlined" label={t("platformPage.gupshupReconcileChipPortal")} />
                            ) : null}
                            {rec.meta?.gupshup_partner_global_numeric_id_set ? (
                              <Chip size="small" color="success" variant="outlined" label={t("platformPage.gupshupReconcileChipNumeric")} />
                            ) : null}
                            {!hasGlobalCred ? (
                              <Typography variant="caption" color="text.secondary">
                                {t("platformPage.gupshupReconcileGlobalEnvNone")}
                              </Typography>
                            ) : null}
                          </Stack>
                          {partnerBase ? (
                            <Link href={partnerBase} target="_blank" rel="noopener noreferrer" variant="caption">
                              {t("platformPage.gupshupReconcilePartnerLinkLabel")}
                            </Link>
                          ) : null}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>

                    {chans.length === 0 ? (
                      <Alert severity="info" variant="outlined">
                        {t("platformPage.gupshupReconcileNoChannels")}
                      </Alert>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          {t("platformPage.gupshupReconcileCompareSectionTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t("platformPage.gupshupReconcileCompareSectionSub")}
                        </Typography>
                        <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                          <Table size="small" sx={WS_TABLE_DENSE_SX}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ minWidth: 200 }}>{t("platformPage.gupshupReconcileColChannel")}</TableCell>
                                <TableCell align="right">{t("platformPage.gupshupReconcileColMsgsPlatformShort")}</TableCell>
                                <TableCell align="right">{t("platformPage.gupshupReconcileColGupRef")}</TableCell>
                                <TableCell sx={{ minWidth: 140 }}>{t("platformPage.gupshupReconcileColAnalysis")}</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>{t("platformPage.gupshupReconcileColSendApi")}</TableCell>
                                <TableCell>{t("platformPage.gupshupReconcileColWalletShort")}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {chans.map((c) => {
                                const usage = c.gupshup?.usage;
                                const wallet = c.gupshup?.wallet;
                                const authOk = c.gupshup?.auth?.ok;
                                const msgApi = c.gupshup?.messaging_api;
                                const pCount = Number(c.platform?.leadsmensagens_count ?? 0);
                                const gApprox = gupshupApproxFromUsage(usage);
                                const kind = gupshupComparisonKind(pCount, usage);
                                const diff = gApprox != null ? pCount - gApprox : null;
                                const compareChip =
                                  kind === "aligned"
                                    ? {
                                        color: "success",
                                        icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
                                        label: t("platformPage.gupshupReconcileCompareAligned"),
                                      }
                                    : kind === "review"
                                      ? {
                                          color: "warning",
                                          icon: <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />,
                                          label: t("platformPage.gupshupReconcileCompareReview"),
                                        }
                                      : kind === "no_total"
                                        ? {
                                            icon: <HelpOutlineIcon sx={{ fontSize: 18 }} />,
                                            label: t("platformPage.gupshupReconcileCompareNoTotal"),
                                          }
                                        : {
                                            color: "error",
                                            icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} />,
                                            label: t("platformPage.gupshupReconcileCompareApiFail"),
                                          };
                                const msgTip = msgApi
                                  ? msgApi.ok
                                    ? t("platformPage.gupshupReconcileMessagingOkShort")
                                    : t("platformPage.gupshupReconcileMessagingFailShort")
                                  : "";
                                return (
                                  <TableRow key={c.origin_id}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>
                                        {c.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        id {c.origin_id}
                                        {c.sourcegupshup ? ` · ${c.sourcegupshup}` : ""}
                                      </Typography>
                                      {!authOk && c.gupshup?.auth?.message ? (
                                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.75 }}>
                                          {c.gupshup.auth.message}
                                        </Typography>
                                      ) : null}
                                      {usage?.partner_note ? (
                                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                                          {usage.partner_note}
                                        </Typography>
                                      ) : null}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight={600}>
                                        {pCount.toLocaleString(locale)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {t("platformPage.gupshupReconcilePlatformCountHint")}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {gApprox != null ? (
                                        <>
                                          <Typography variant="body2" fontWeight={600}>
                                            {gApprox.toLocaleString(locale)}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {t("platformPage.gupshupReconcileGupRefHint")}
                                          </Typography>
                                        </>
                                      ) : (
                                        <>
                                          <Typography variant="body2" color="text.secondary">
                                            —
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {usage?.ok === false
                                              ? t("platformPage.gupshupReconcileGupRefUnavailable")
                                              : t("platformPage.gupshupReconcileGupRefEmpty")}
                                          </Typography>
                                        </>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        {...(compareChip.color ? { color: compareChip.color } : {})}
                                        variant={kind === "aligned" ? "filled" : "outlined"}
                                        icon={compareChip.icon}
                                        label={compareChip.label}
                                        sx={{
                                          fontWeight: 600,
                                          "& .MuiChip-icon": { ml: 0.5 },
                                          ...(kind === "no_total" ? { borderColor: "divider", color: "text.secondary" } : {}),
                                        }}
                                      />
                                      {kind === "review" && diff != null ? (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                          {t("platformPage.gupshupReconcileCompareDiffHint", {
                                            diff: diff > 0 ? `+${diff.toLocaleString(locale)}` : diff.toLocaleString(locale),
                                          })}
                                        </Typography>
                                      ) : null}
                                    </TableCell>
                                    <TableCell>
                                      {msgApi ? (
                                        <Tooltip title={msgTip}>
                                          <Stack direction="row" alignItems="center" spacing={0.75}>
                                            {msgApi.ok ? (
                                              <CheckCircleOutlineIcon fontSize="small" color="success" />
                                            ) : (
                                              <WarningAmberOutlinedIcon fontSize="small" color="warning" />
                                            )}
                                            <Typography variant="body2">
                                              {msgApi.ok
                                                ? t("platformPage.gupshupReconcileSendOk")
                                                : t("platformPage.gupshupReconcileSendIssue")}
                                            </Typography>
                                          </Stack>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          —
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {wallet?.ok ? (
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                          <CheckCircleOutlineIcon fontSize="small" color="success" />
                                          <Typography variant="body2">{t("platformPage.gupshupReconcileWalletOk")}</Typography>
                                        </Stack>
                                      ) : wallet ? (
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                          <WarningAmberOutlinedIcon fontSize="small" color="warning" />
                                          <Typography variant="body2">{t("platformPage.gupshupReconcileWalletIssue")}</Typography>
                                        </Stack>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          —
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )}

                    {Array.isArray(rec.notes) && rec.notes.length > 0 ? (
                      <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                          {t("platformPage.gupshupReconcileFootnotesTitle")}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                          {rec.notes.map((n, idx) => (
                            <Typography component="li" variant="body2" color="text.secondary" key={`${idx}-${String(n).slice(0, 24)}`}>
                              {n}
                            </Typography>
                          ))}
                        </Box>
                      </Alert>
                    ) : null}
                  </>
                );
              })()}
            </Stack>
          ) : null}

          <Card
            variant="outlined"
            sx={{
              mt: 3,
              ...WS_SURFACE_SX,
              borderColor: "error.light",
              bgcolor: (th) => alpha(th.palette.error.main, 0.04),
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" color="error" fontWeight={700} gutterBottom>
                {t("platformPage.excludeWorkspaceSectionTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                {t("platformPage.excludeWorkspaceSectionHint")}
              </Typography>
              <Button color="error" variant="outlined" onClick={() => setExcludeDialogOpen(true)} disabled={loading}>
                {t("platformPage.excludeWorkspaceOpenDialog")}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default WorkspaceDetailPage;
